import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  Select,
  InputLabel,
  FormControl,
  Grid,
  FormHelperText,
  Skeleton,
  Avatar,
  Badge,
  Stack,
  Snackbar,
  InputAdornment,
  Checkbox,
  ListItemText,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { timeOnly, formatDateTime, formatServerDateTime } from '../utils/timezone.js';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TagIcon from '@mui/icons-material/Tag';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import NoteOutlinedIcon from '@mui/icons-material/NoteOutlined';
import ConfirmDialog from '../components/ConfirmDialog.js';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import Navbar from '../components/Navbar.js';
import { COUNTRIES, detectDefaultCountry } from '../components/PhoneField.js';
import FacadeManager from '../components/FacadeManager.js';
import OrgSettingsCards from '../components/OrgSettingsCards.js';
import OrgProfileCard from '../components/OrgProfileCard.js';
import ShareButton from '../components/ShareButton.js';
import UsernamePasswordFields from '../components/UsernamePasswordFields.js';
import SendInviteDialog from '../components/SendInviteDialog.js';
import SchedulerDialog from '../components/SchedulerDialog.js';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import { ENDPOINTS, apiCall, API_BASE } from '../utils/api.js';
import { useAppConfig } from '../utils/useAppConfig.js';
import { sortCategoriesByOrder, computeReorder } from '../utils/categoryOrder.js';

/**
 * Parse an API error response body into a human-readable string.
 * Handles: { detail }, { errors: string }, { errors: { field: [msgs] } },
 *          { message }, { non_field_errors }, raw strings, and JSON fallback.
 */
function parseApiError(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err.detail) return err.detail;
  if (err.message) return err.message;
  if (err.non_field_errors) return [].concat(err.non_field_errors).join(' ');
  if (err.errors) {
    if (typeof err.errors === 'string') return err.errors;
    if (typeof err.errors === 'object') {
      return Object.entries(err.errors)
        .map(([field, msgs]) => {
          const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
          const msg = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
          return `${fieldLabel}: ${msg}`;
        })
        .join(' · ');
    }
  }
  // Last resort: try to extract any string values from the object
  try {
    const vals = Object.values(err).flat().filter((v) => typeof v === 'string');
    if (vals.length) return vals.join(' · ');
  } catch (_) { /* ignore */ }
  return fallback;
}

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const statusConfig = {
  active:   { label: 'Active',      bg: '#e8f5e9', text: '#2e7d32', borderColor: '#4caf50' },
  inactive: { label: 'Inactive',    bg: '#f5f5f5', text: '#616161', borderColor: '#bdbdbd' },
  checkin:  { label: 'Checked In',  bg: '#e3f2fd', text: '#1565c0', borderColor: '#42a5f5' },
  checkout: { label: 'Checked Out', bg: '#ede7f6', text: '#4527a0', borderColor: '#9575cd' },
  cancel:   { label: 'Cancelled',   bg: '#fce4ec', text: '#c62828', borderColor: '#ef9a9a' },
};

// ---------------------------------------------------------------------------
// NotesPanel — shown below an appointment row when the admin clicks "Notes"
// ---------------------------------------------------------------------------
const NOTE_MAX_CHARS = 1000;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

function NotesPanel({ appt, token }) {
  // File attachments are a server-side feature flag (NOTE_ATTACHMENTS_ENABLED). Hide the whole
  // attach affordance when it's off — the API rejects uploads regardless, this just avoids
  // offering something that can't work.
  const { note_attachments_enabled: attachmentsEnabled } = useAppConfig();
  const API_BASE_NOTES = API_BASE;
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteFile, setNoteFile] = useState(null); // { name, mime, data (base64 data-url) }
  const [submitting, setSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');
  const fileRef = React.useRef(null);
  // keep a stable ref to the latest token so effects/callbacks always use current value
  const tokenRef = React.useRef(token);
  tokenRef.current = token;
  const apptIdRef = React.useRef(appt.id);
  apptIdRef.current = appt.id;

  const doFetch = React.useRef(async () => {
    setLoadingNotes(true);
    try {
      const data = await apiCall(ENDPOINTS.APPOINTMENT_NOTES(apptIdRef.current));
      setNotes(data);
    } catch (e) { /* ignore */ }
    setLoadingNotes(false);
  });

  useEffect(() => {
    (async () => { await doFetch.current(); })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const mime = file.type || 'application/octet-stream';
    if (!ALLOWED_MIME.includes(mime)) {
      setNoteError('Only image files (JPEG, PNG, GIF, WebP) and PDF files are allowed.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setNoteError('File must be under 5 MB.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setNoteError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setNoteFile({ name: file.name, mime, data: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleAddNote = async () => {
    if (!noteText.trim() && !noteFile) {
      setNoteError('Please add a note or attach a file.');
      return;
    }
    setNoteError('');
    setSubmitting(true);
    try {
      const body = {
        content: noteText.trim(),
        ...(noteFile ? { file_data: noteFile.data, file_name: noteFile.name, file_mime: noteFile.mime } : {}),
      };
      await apiCall(ENDPOINTS.APPOINTMENT_NOTES(apptIdRef.current), {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setNoteText('');
      setNoteFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await doFetch.current();
    } catch (err) {
      setNoteError(err.message || 'Failed to add note');
    }
    setSubmitting(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await apiCall(ENDPOINTS.APPOINTMENT_NOTES(apptIdRef.current) + noteId + '/', {
        method: 'DELETE',
      });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch { /* ignore */ }
  };

  const handleDownloadFile = async (noteId) => {
    try {
      const { file_data, file_name } = await apiCall(ENDPOINTS.APPOINTMENT_NOTE_FILE(apptIdRef.current, noteId));
      if (!file_data) return;
      const a = document.createElement('a');
      a.href = file_data;
      a.download = file_name || 'attachment';
      a.click();
    } catch { /* ignore */ }
  };

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.07)' }}>
      <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, display: 'block' }}>
        Notes &amp; Attachments
      </Typography>

      {/* Existing notes */}
      {loadingNotes ? (
        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 2, mb: 1 }} />
      ) : notes.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>No notes yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
          {notes.map((note) => (
            <Paper
              key={note.id}
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: note.is_admin_note ? '1px solid #bbdefb' : '1px solid #e0e0e0',
                background: note.is_admin_note ? '#e3f2fd' : '#fafafa',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <NoteOutlinedIcon sx={{ fontSize: 16, mt: 0.2, color: note.is_admin_note ? '#1565c0' : '#9e9e9e', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {note.content && (
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.82rem' }}>{note.content}</Typography>
                )}
                {note.has_file && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleDownloadFile(note.id)}
                    sx={{ mt: 0.5, fontSize: 11, py: 0.25, px: 0.75, borderRadius: 1.5, textTransform: 'none', color: 'primary.main' }}
                  >
                    {note.file_name || 'Download file'}
                  </Button>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                  {note.is_admin_note ? '🛡 Admin' : '👤 User'} · {note.added_by_name || ''} · {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                </Typography>
              </Box>
              <Tooltip title="Delete note">
                <IconButton size="small" onClick={() => handleDeleteNote(note.id)} sx={{ flexShrink: 0, color: 'error.light' }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      {/* Add note form */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          multiline
          minRows={2}
          placeholder="Add a note (optional)"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value.slice(0, NOTE_MAX_CHARS))}
          inputProps={{ maxLength: NOTE_MAX_CHARS }}
          helperText={`${noteText.length}/${NOTE_MAX_CHARS}`}
          FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
        />
        {noteError && <Typography variant="caption" color="error">{noteError}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {attachmentsEnabled && (
            <>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
            onClick={() => fileRef.current && fileRef.current.click()}
            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12 }}
          >
            {noteFile ? noteFile.name : 'Attach file'}
          </Button>
          {noteFile && (
            <Tooltip title="Remove attachment">
              <IconButton size="small" onClick={() => { setNoteFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
            </>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<NoteAddOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={handleAddNote}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: 12,
              background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
            }}
          >
            {submitting ? 'Saving…' : 'Add Note'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function AppointmentRow({ appt, index, totalCount, onAction, loading, innerRef, token }) {
  const cfg = statusConfig[appt.status] || statusConfig.active;
  const canCheckin = appt.status === 'active' || appt.status === 'inactive';
  const canCheckout = appt.status === 'checkin';   // already checked-in → mark served
  const canCancel = appt.status !== 'cancel';
  // Reorder (↑/↓) only applies to the live walk-in queue — i.e. active, unscheduled rows.
  // Checked-in/out, cancelled, inactive and scheduled rows have left the queue, so it's hidden.
  const canMove = !appt.is_scheduled && appt.status === 'active';
  // Prefer explicit first/last name supplied by the API. Fall back to username.
  const displayName = `${(appt.user_first_name || appt.first_name || '').trim()} ${(appt.user_last_name || '').trim()}`.trim() || appt.username || `User #${appt.user}`;
  const [showNotes, setShowNotes] = useState(false);

  return (
    <Paper
      elevation={0}
      ref={innerRef}
      sx={{
        p: { xs: 1, sm: 2 },
        mb: 1,
        borderRadius: 3,
        border: `1px solid ${cfg.borderColor}`,
        borderLeft: `4px solid ${cfg.borderColor}`,
        background: cfg.bg,
        opacity: appt.status === 'cancel' ? 0.55 : 1,
        transition: 'box-shadow 0.12s',
        '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.06)' },
      }}
    >
  <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
        {/* Left: avatar + appointment id (stacked) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0, width: { xs: 48, sm: 60 } }}>
          <Avatar
            sx={{
              width: { xs: 32, sm: 38 },
              height: { xs: 32, sm: 38 },
              background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
              fontWeight: 700,
              fontSize: { xs: 12, sm: 14 },
            }}
          >
            {appt.is_scheduled ? '📅' : (index + 1)}
          </Avatar>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 11 } }}>#{appt.id}</Typography>
        </Box>

        {/* Main info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1rem' },
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
              title={displayName}
            >
              {displayName}
            </Typography>
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                bgcolor: cfg.bg,
                color: cfg.text,
                border: `1px solid ${cfg.borderColor}`,
                fontWeight: 600,
                fontSize: 11,
                height: 20,
              }}
            />
          </Box>
          <Stack direction="column" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              {/* Email and phone should be visible on mobile — allow wrapping to avoid overlap with actions */}
              {appt.user_email && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' }, whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: '100%', sm: '40ch' } }}
                  title={appt.user_email}
                >
                  ✉️ {appt.user_email}
                </Typography>
              )}

              {appt.user_phone && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' }, whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: { xs: '100%', sm: '20ch' } }}
                  title={appt.user_phone}
                >
                  📞 {appt.user_phone}
                </Typography>
              )}
            </Box>

            {appt.scheduled_time && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' } }}>
                  {appt.scheduled_time_display
                    ? appt.scheduled_time_display
                    : appt.scheduled_time_with_category_tz
                    ? appt.scheduled_time_with_category_tz
                    : formatServerDateTime(appt.scheduled_time)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

    {/* Action buttons */}
  <Box sx={{
            display: 'flex',
            gap: 0.75,
            flexShrink: 0,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            order: { xs: 3, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            mt: { xs: 1, sm: 0 },
            px: { xs: 0, sm: 'inherit' },
          }}>
          {/* Move Up/Down — only for the live (active) walk-in queue */}
          {canMove && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Move Up">
                <span>
                  <IconButton
                    size="small"
                    disabled={loading || !canMove || index === 0}
                    onClick={() => onAction('move-up', appt, index)}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: 30, height: 30, '&:not(:disabled):hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move Down">
                <span>
                  <IconButton
                    size="small"
                    disabled={loading || !canMove || index === totalCount - 1}
                    onClick={() => onAction('move-down', appt, index)}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: 30, height: 30, '&:not(:disabled):hover': { borderColor: 'primary.main', color: 'primary.main' } }}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* Reschedule — scheduled appointments only, moves to a new available slot */}
          {appt.is_scheduled && appt.status === 'active' && (
            <Tooltip title="Reschedule">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loading}
                  onClick={() => onAction('reschedule', appt)}
                  startIcon={<EditCalendarIcon sx={{ fontSize: 16 }} />}
                  sx={{ borderRadius: 5, textTransform: 'none', minWidth: 0 }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Reschedule</Box>
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Check-In — icon on mobile, icon+label on desktop */}
          {canCheckin && (
            <Tooltip title="Check In">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={loading}
                  onClick={() => onAction('checkin', appt)}
                  sx={{
                    borderRadius: 2,
                    minWidth: { xs: 36, sm: 'auto' },
                    px: { xs: 0, sm: 1.5 },
                    py: 0.5,
                    background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
                    '&:hover': { opacity: 0.95 },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5, fontSize: 12 }}>Check In</Box>
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Checkout — icon on mobile, icon+label on desktop */}
          {canCheckout && (
            <Tooltip title="Checkout">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={loading}
                  onClick={() => onAction('checkout', appt)}
                  sx={{
                    borderRadius: 2,
                    minWidth: { xs: 36, sm: 'auto' },
                    px: { xs: 0, sm: 1.5 },
                    py: 0.5,
                    background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
                    '&:hover': { opacity: 0.95 },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 15 }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5, fontSize: 12 }}>Checkout</Box>
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Cancel — icon on mobile, icon+label on desktop */}
          {canCancel && (
            <Tooltip title="Cancel">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={loading}
                  onClick={() => onAction('cancel', appt)}
                  sx={{
                    borderRadius: 2,
                    minWidth: { xs: 36, sm: 'auto' },
                    px: { xs: 0, sm: 1.5 },
                    py: 0.5,
                  }}
                >
                  <CancelOutlinedIcon sx={{ fontSize: 15 }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5, fontSize: 12 }}>Cancel</Box>
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Notes toggle — icon on mobile, icon+label on desktop */}
          <Tooltip title={showNotes ? 'Hide Notes' : 'Notes'}>
            <Button
              size="small"
              variant={showNotes ? 'contained' : 'outlined'}
              onClick={() => setShowNotes((v) => !v)}
              sx={{
                borderRadius: 2,
                minWidth: { xs: 36, sm: 'auto' },
                px: { xs: 0, sm: 1.25 },
                py: 0.5,
                textTransform: 'none',
                ...(showNotes ? {
                  background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
                } : {}),
              }}
            >
              <NoteOutlinedIcon sx={{ fontSize: 15 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5, fontSize: 12 }}>
                Notes{showNotes ? '' : ''}
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {showNotes ? <ExpandLessIcon sx={{ fontSize: 13, ml: 0.25 }} /> : <ExpandMoreIcon sx={{ fontSize: 13, ml: 0.25 }} />}
              </Box>
            </Button>
          </Tooltip>
        </Box>
      </Box>
      {showNotes && <NotesPanel appt={appt} token={token} />}
    </Paper>
  );
}

function AppointmentList({ category, apptType, refreshKey = null }) {
  // apptType: 'unscheduled' | 'scheduled'
  const [appointments, setAppointments] = useState([]);
  const itemRefs = useRef({});
  const [dateFilter, setDateFilter] = useState('all');
  const [searchInput, setSearchInput] = useState(''); // immediate text field value
  const [search, setSearch] = useState('');           // debounced term sent to the API
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  // Infinite scroll: hold the next-page URL + total count instead of loading every page upfront.
  const [nextUrl, setNextUrl] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  // Monotonic request id: while searching, responses can arrive out of order — only the
  // newest request is allowed to write state, so stale results never overwrite fresh ones.
  const fetchSeqRef = useRef(0);
  // Abort controller for the in-flight list request, so a new search cancels the previous one
  // (the browser closes the connection and the server stops working on the abandoned query).
  const abortRef = useRef(null);
  // Multi-select bulk actions (offered on the Active / Checked In / Checked Out tabs).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllQueue, setSelectAllQueue] = useState(false); // whole server-side queue, not just loaded
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // scheduled appt being rescheduled
  const bulkEnabled = ['active', 'checkin', 'checkout'].includes(statusFilter);
  // Only forward transitions are offered from a given tab; backward moves aren't shown (they'd fail).
  // Flow: active -> checked-in -> checked-out; cancel is a valid exit from any of these.
  const FORWARD_BY_TAB = {
    active: ['checkin', 'checkout', 'cancel'],
    checkin: ['checkout', 'cancel'],
    checkout: ['cancel'],
  };
  const STATUS_LABEL = { checkin: 'Check in', checkout: 'Check out', cancel: 'Cancel' };
  const forwardStatuses = FORWARD_BY_TAB[statusFilter] || [];
  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllQueue(false);
  }, []);

  // Scheduled lists offer a date filter; derive the dates from the appointments loaded so far.
  const availableDates = React.useMemo(() => {
    if (apptType !== 'scheduled') return [];
    return Array.from(new Set((appointments || [])
      .filter((a) => a.scheduled_time)
      .map((a) => (typeof a.scheduled_time === 'string' ? a.scheduled_time.split('T')[0] : ''))
      .filter(Boolean))).sort();
  }, [appointments, apptType]);

  const token = localStorage.getItem('accessToken');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAppointments = useCallback(async () => {
    // The appointments API rejects an inactive category with a 400, so don't bother asking —
    // show the same guidance directly instead of firing a request we know will fail.
    if (category.status !== 'active') {
      setAppointments([]);
      setNextUrl(null);
      setError('This category appears inactive or invalid. Turn the category to Active to view appointments.');
      setLoading(false);
      return;
    }
    const seq = ++fetchSeqRef.current;
    // Cancel any in-flight list request before starting a new one.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError('');
    try {
      let endpoint;
      if (apptType === 'scheduled') {
        endpoint = ENDPOINTS.APPOINTMENTS_SCHEDULED_LIST(100) + `&category_id=${category.id}&status=${statusFilter}`;
      } else {
        endpoint = ENDPOINTS.APPOINTMENTS_UNSCHEDULED_LIST(100) + `&category_id=${category.id}&status=${statusFilter}`;
      }
      if (search && search.trim()) endpoint += `&search=${encodeURIComponent(search.trim())}`;
      // Load just the first page; more pages load as the user scrolls (see loadMore).
      const data = await apiCall(endpoint, { signal: controller.signal });
      if (seq !== fetchSeqRef.current) return; // a newer search superseded this response
      setAppointments(data.results || []);
      setNextUrl(data.next || null);
      setTotalCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
    } catch (err) {
      // A superseded/cancelled request is expected — swallow it silently.
      if (err && (err.name === 'AbortError' || controller.signal.aborted)) return;
      if (seq !== fetchSeqRef.current) return; // ignore errors from superseded requests
      // Map certain backend validation errors to friendly UI guidance.
      let friendly = err.message || 'Failed to load appointments.';
      try {
        const body = err.body || {};
        const msg = (err.message || '').toLowerCase();
        // If the API complains about category_id (invalid or inactive), show actionable guidance
        if (body && (body.category_id || body.category || msg.includes('category_id') || msg.includes('one or more category ids are invalid') || msg.includes('invalid category'))) {
          friendly = 'This category appears inactive or invalid. Turn the category to Active to view appointments.';
        }
      } catch (e) {
        // ignore
      }
      // If this is a category-related error, clear the appointments list in the UI
      try {
        const body = err.body || {};
        const msg = (err.message || '').toLowerCase();
        if (body && (body.category_id || body.category || msg.includes('category_id') || msg.includes('one or more category ids are invalid') || msg.includes('invalid category'))) {
          setAppointments([]);
          setNextUrl(null);
        }
      } catch (e) {
        // ignore
      }
      setError(friendly);
    }
    setLoading(false);
  }, [category.id, category.status, apptType, statusFilter, token, search]);

  // Debounce the search box: push the typed value into `search` (which drives fetchAppointments)
  // after a short pause, so we don't fire a request on every keystroke. Require 2+ chars before
  // hitting the API (a single character matches almost everything and is needlessly heavy).
  useEffect(() => {
    const trimmed = searchInput.trim();
    const effective = trimmed.length >= 2 ? trimmed : '';
    if (effective === search) return undefined;
    const t = setTimeout(() => setSearch(effective), 250);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  useEffect(() => {
    // Call fetchAppointments inside an async IIFE to avoid synchronous setState within the effect body
    (async () => {
      await fetchAppointments();
    })();
  }, [fetchAppointments]);

  // Abort any in-flight request when this list unmounts (e.g. switching category/tab).
  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  // Re-fetch when refreshKey changes (if provided)
  useEffect(() => {
    if (refreshKey == null) return;
    (async () => {
      await fetchAppointments();
    })();
  }, [refreshKey]);

  // Infinite scroll: append the next page instead of loading everything upfront.
  const loadMore = useCallback(async () => {
    if (!nextUrl || loadingMore) return;
    setLoadingMore(true);
    try {
      const pageData = await apiCall(nextUrl);
      setAppointments((prev) => (prev || []).concat(pageData.results || []));
      setNextUrl(pageData.next || null);
    } catch (e) {
      // Leave the already-loaded rows in place; a failed page-fetch shouldn't wipe the list.
    }
    setLoadingMore(false);
  }, [nextUrl, loadingMore]);

  // Observe a sentinel at the end of the list; when it scrolls into view, pull the next page.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting && nextUrl && !loadingMore && !loading) {
          loadMore();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [nextUrl, loadingMore, loading, loadMore]);

  // If the derived availableDates change and the currently selected date is
  // no longer present, reset the date filter to 'all'. This is kept outside
  // of fetchAppointments to avoid capturing dateFilter in that callback's
  // dependency array (which would break memoization rules).
  useEffect(() => {
    if (dateFilter && dateFilter !== 'all' && Array.isArray(availableDates) && !availableDates.includes(dateFilter)) {
      // Schedule the reset to the next tick to avoid synchronous setState inside an effect
      // which can trigger cascading renders and trips the eslint rule.
      const t = setTimeout(() => setDateFilter('all'), 0);
      return () => clearTimeout(t);
    }
  }, [availableDates, dateFilter]);

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });
  // Compute the currently displayed appointments after applying the date filter
  const filteredAppointments = (appointments || []).filter((a) =>
    dateFilter === 'all' || !a.scheduled_time || a.scheduled_time.split('T')[0] === dateFilter
  );
  // When date-filtering scheduled rows we can only count what's loaded; otherwise trust the server total.
  const dateFiltered = apptType === 'scheduled' && dateFilter !== 'all';
  const displayCount = dateFiltered ? filteredAppointments.length : totalCount;

  // Format date like "3rd March 2026"
  const formatDateLabel = (isoDate) => {
    try {
      // isoDate is a plain 'YYYY-MM-DD'. `new Date('YYYY-MM-DD')` parses as UTC midnight,
      // which renders as the previous day in negative-offset zones (e.g. Canada/Eastern).
      // Split and build a local-time Date so the label matches the stored calendar date.
      let d;
      const m = typeof isoDate === 'string' && isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      } else {
        d = new Date(isoDate);
      }
      if (Number.isNaN(d.getTime())) return isoDate;
      const day = d.getDate();
      let ord = 'th';
      if (!(day % 100 >= 11 && day % 100 <= 13)) {
        if (day % 10 === 1) ord = 'st';
        else if (day % 10 === 2) ord = 'nd';
        else if (day % 10 === 3) ord = 'rd';
      }
      const month = d.toLocaleString(undefined, { month: 'long' });
      return `${day}${ord} ${month} ${d.getFullYear()}`;
    } catch (e) {
      return isoDate;
    }
  };

  const handleAction = async (action, appt, index) => {
    // Reschedule opens a dialog rather than hitting an endpoint directly.
    if (action === 'reschedule') {
      setRescheduleTarget(appt);
      return;
    }
    setActionLoading(true);
    try {
      if (action === 'checkin') {
        await apiCall(ENDPOINTS.APPOINTMENT_CHECKIN(appt.id), {
          method: 'POST',
        });
      } else if (action === 'checkout') {
        // Use dedicated checkout endpoint — records checkout_time, no counter adjustment
        await apiCall(ENDPOINTS.APPOINTMENT_CHECKOUT(appt.id), {
          method: 'POST',
        });
      } else if (action === 'cancel') {
        await apiCall(ENDPOINTS.APPOINTMENT_CANCEL(appt.id), {
          method: 'POST',
        });
      } else if (action === 'move-up') {
        const previous = index >= 2 ? appointments[index - 2] : null;
        await apiCall(ENDPOINTS.APPOINTMENT_MOVE(appt.id), {
          method: 'POST',
          body: JSON.stringify({ previous_appointment_id: previous ? previous.id : null }),
        });
      } else if (action === 'move-down') {
        const previous = appointments[index + 1];
        await apiCall(ENDPOINTS.APPOINTMENT_MOVE(appt.id), {
          method: 'POST',
          body: JSON.stringify({ previous_appointment_id: previous ? previous.id : null }),
        });
      }
      
      // Handle move animations
      if (action === 'move-up' || action === 'move-down') {
        // Capture previous bounding rects
        const prevRects = {};
        (appointments || []).forEach((a) => {
          const el = itemRefs.current && itemRefs.current[a.id];
          if (el && el.getBoundingClientRect) prevRects[a.id] = el.getBoundingClientRect();
        });

        // Determine the visible neighbor to swap with (respecting filters)
        const visibleIds = (filteredAppointments || []).map((a) => a.id);
        const visIdx = visibleIds.indexOf(appt.id);
        let targetId = null;
        if (action === 'move-up' && visIdx > 0) targetId = visibleIds[visIdx - 1];
        if (action === 'move-down' && visIdx >= 0 && visIdx < visibleIds.length - 1) targetId = visibleIds[visIdx + 1];

        if (targetId) {
          const nextOrder = (appointments || []).slice();
          const idxCurr = nextOrder.findIndex((x) => x.id === appt.id);
          const idxTarget = nextOrder.findIndex((x) => x.id === targetId);
          if (idxCurr !== -1 && idxTarget !== -1) {
            [nextOrder[idxCurr], nextOrder[idxTarget]] = [nextOrder[idxTarget], nextOrder[idxCurr]];
            // Apply updated order immediately
            setAppointments(nextOrder);

            // After render, animate from old -> new positions
            requestAnimationFrame(() => {
              Object.keys(prevRects).forEach((id) => {
                const el = itemRefs.current && itemRefs.current[id];
                if (!el) return;
                const prev = prevRects[id];
                const now = el.getBoundingClientRect();
                const dy = prev.top - now.top;
                if (dy) {
                  const dx = dy > 0 ? 6 : -6;
                  el.style.transform = `translateY(${dy}px) translateX(${dx}px)`;
                  el.style.opacity = '0.92';
                    el.getBoundingClientRect();
                    el.style.transition = 'transform 420ms cubic-bezier(0.22,1,0.36,1), opacity 420ms ease';
                    requestAnimationFrame(() => {
                      el.style.transform = '';
                      el.style.opacity = '';
                    });
                    setTimeout(() => { if (el) { el.style.transition = ''; el.style.transform = ''; el.style.opacity = ''; } }, 480);
                  }
                });
              });
            }
          }

          showToast('↕️ Queue updated');
        } else {
          const toastMsg =
            action === 'checkin'  ? '✅ Checked in' :
            action === 'checkout' ? '🏁 Checked out' :
            action === 'cancel'   ? '❌ Cancelled' : 'Done';

          // Animate the card: slide right + fade out, then remove from list
          const el = itemRefs.current && itemRefs.current[appt.id];
          const DURATION = 380;
          if (el) {
            el.style.transition = `transform ${DURATION}ms cubic-bezier(0.4,0,1,1), opacity ${DURATION}ms ease, max-height ${DURATION}ms ease, margin ${DURATION}ms ease`;
            el.style.transform = 'translateX(60px)';
            el.style.opacity = '0';
            el.style.maxHeight = el.offsetHeight + 'px';
            el.style.overflow = 'hidden';
            // After slide-out, collapse height to zero so space closes smoothly
            setTimeout(() => {
              if (el) {
                el.style.maxHeight = '0';
                el.style.marginBottom = '0';
                el.style.paddingTop = '0';
                el.style.paddingBottom = '0';
              }
            }, DURATION - 20);
            setTimeout(() => {
              setAppointments((prev) => prev.filter((a) => a.id !== appt.id));
            }, DURATION + 80);
          } else {
            // No DOM ref — just remove immediately
            setAppointments((prev) => prev.filter((a) => a.id !== appt.id));
          }

          showToast(toastMsg);
        }
    } catch (err) {
      showToast(err.message || 'Action failed', 'error');
    }
    setActionLoading(false);
  };

  // ---- Multi-select bulk actions ----
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      // Leaving "whole queue" mode: materialize the loaded rows first so unchecking
      // one keeps the others selected rather than collapsing to a single id.
      const next = selectAllQueue ? new Set(filteredAppointments.map((a) => a.id)) : new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSelectAllQueue(false); // an individual toggle exits "whole queue" mode
  };

  const allLoadedSelected = filteredAppointments.length > 0
    && filteredAppointments.every((a) => selectedIds.has(a.id));

  const toggleSelectAllLoaded = () => {
    if (selectAllQueue || allLoadedSelected) {
      clearSelection();
    } else {
      setSelectedIds(new Set(filteredAppointments.map((a) => a.id)));
    }
  };

  const selectedCount = selectAllQueue ? totalCount : selectedIds.size;

  const runBulk = async (action) => {
    if (selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const body = { action };
      if (selectAllQueue) {
        // Whole queue — let the server resolve every matching row across all pages.
        body.select_all = true;
        body.source_category_id = category.id;
        body.source_status = statusFilter;
        body.is_scheduled = apptType === 'scheduled';
        if (search && search.trim()) body.search = search.trim(); // match the visible (searched) set
      } else {
        body.appointment_ids = Array.from(selectedIds);
      }
      const res = await apiCall(ENDPOINTS.APPOINTMENTS_BULK_ACTION, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      clearSelection();
      await fetchAppointments();
      showToast(res && res.detail ? res.detail : 'Done');
    } catch (err) {
      showToast(err.message || 'Bulk action failed', 'error');
    }
    setBulkBusy(false);
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {/* Status filter as clickable tabs for faster switching */}
        <Tabs
          value={statusFilter}
          onChange={(_, v) => { clearSelection(); setStatusFilter(v); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minWidth: 240,
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 40, py: 0.5 },
            '& .MuiTabs-indicator': { height: 3, borderRadius: 2, background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)' },
          }}
        >
          <Tab label="Active" value="active" />
          <Tab label="Checked In" value="checkin" />
          <Tab label="Checked Out" value="checkout" />
          <Tab label="Inactive" value="inactive" />
          <Tab label="Cancelled" value="cancel" />
        </Tabs>

        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchAppointments}
            disabled={loading}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        {/* Search by name, email or phone (server-side, spans the whole queue) */}
        <TextField
          size="small"
          placeholder="Search name, email, phone, #token…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 220, flexGrow: { xs: 1, sm: 0 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (loading && search) ? (
              <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
            ) : searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} edge="end" aria-label="clear search">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {/* Date filter: only for scheduled appointment lists and when dates are available */}
        {apptType === 'scheduled' && Array.isArray(availableDates) && availableDates.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id={`date-filter-label-${category.id}`}>Date</InputLabel>
            <Select
              labelId={`date-filter-label-${category.id}`}
              value={dateFilter}
              label="Date"
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <MenuItem value="all">All dates</MenuItem>
              {availableDates.map((d) => (
                <MenuItem key={d} value={d}>{formatDateLabel(d)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {/* Show the server-side total for the current status; a date filter narrows to the loaded rows. */}
          <Typography variant="body2" color="text.secondary">
            {displayCount} appointment{displayCount !== 1 ? 's' : ''}
          </Typography>
          {appointments.length > 0 && (
            <Chip
              label={displayCount}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          )}
        </Box>
      </Box>

      {/* Bulk multi-select toolbar (Active / Checked In / Checked Out tabs) */}
      {bulkEnabled && !loading && filteredAppointments.length > 0 && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap',
            px: selectedCount > 0 ? 1.5 : 0.5, py: selectedCount > 0 ? 1 : 0,
            borderRadius: 2,
            bgcolor: selectedCount > 0 ? 'action.hover' : 'transparent',
            border: selectedCount > 0 ? '1px solid' : '1px solid transparent',
            borderColor: selectedCount > 0 ? 'divider' : 'transparent',
          }}
        >
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={selectAllQueue || allLoadedSelected}
                indeterminate={!selectAllQueue && !allLoadedSelected && selectedIds.size > 0}
                onChange={toggleSelectAllLoaded}
              />
            )}
            label={<Typography variant="body2">Select all</Typography>}
          />
          {selectedCount > 0 && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {selectedCount} selected
              </Typography>
              {!selectAllQueue && allLoadedSelected && totalCount > filteredAppointments.length && (
                <Button size="small" onClick={() => setSelectAllQueue(true)} sx={{ textTransform: 'none' }}>
                  Select all {totalCount} in queue
                </Button>
              )}
              {selectAllQueue && (
                <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none' }}>
                  Clear
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {bulkBusy && <CircularProgress size={18} sx={{ mr: 0.5 }} />}
              {/* Forward status transitions for this tab (backward ones are not offered). */}
              {forwardStatuses.map((s) => (
                <Button
                  key={s}
                  size="small"
                  variant="outlined"
                  color={s === 'cancel' ? 'error' : 'primary'}
                  disabled={bulkBusy}
                  onClick={() => runBulk(s)}
                  sx={{ borderRadius: 5, textTransform: 'none' }}
                >
                  {STATUS_LABEL[s]}
                </Button>
              ))}
            </>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Only show skeletons on the first load (empty list). While re-searching/refreshing an
          already-populated list, keep the rows visible and dim them — avoids a jarring flash. */}
      {loading && appointments.length === 0 && (
        <Box>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1, borderRadius: 3 }} />
          ))}
        </Box>
      )}

      {!loading && filteredAppointments.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <EventAvailableOutlinedIcon sx={{ fontSize: 48, opacity: 0.12, mb: 1, color: (theme) => theme.palette.custom ? theme.palette.custom.teal : '#00C4CC' }} />
          <Typography variant="body2" color="text.secondary">
            {search ? <>No matches for <strong>{search}</strong>.</> : <>No <strong>{statusFilter}</strong> {apptType} appointments.</>}
          </Typography>
        </Box>
      )}

      {filteredAppointments.length > 0 && (
        <Fade in timeout={250}>
          <Box sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease', pointerEvents: loading ? 'none' : 'auto' }}>
            {filteredAppointments.map((appt, i) => {
              const row = (
                <AppointmentRow
                  key={appt.id}
                  appt={appt}
                  index={i}
                  totalCount={filteredAppointments.length}
                  onAction={handleAction}
                  loading={actionLoading}
                  token={token}
                  innerRef={(el) => {
                    if (!itemRefs.current) itemRefs.current = {};
                    if (el) itemRefs.current[appt.id] = el;
                    else if (itemRefs.current[appt.id]) delete itemRefs.current[appt.id];
                  }}
                />
              );
              if (!bulkEnabled) return row;
              const checked = selectAllQueue || selectedIds.has(appt.id);
              return (
                <Box key={appt.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => toggleRow(appt.id)}
                    sx={{ flexShrink: 0 }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>{row}</Box>
                </Box>
              );
            })}
          </Box>
        </Fade>
      )}

      {/* Sentinel: scrolling it into view auto-loads the next page; the button is a manual fallback. */}
      {!loading && nextUrl && (
        <Box ref={sentinelRef} sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Showing {appointments.length} of {totalCount}
          </Typography>
          {loadingMore ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">Loading more…</Typography>
            </Box>
          ) : (
            <Button variant="outlined" size="small" onClick={loadMore} disabled={loadingMore}>
              Load more
            </Button>
          )}
        </Box>
      )}

      {/* Reschedule dialog: reuses the booking slot-picker; posts to the reschedule endpoint. */}
      {rescheduleTarget && (
        <SchedulerDialog
          open={Boolean(rescheduleTarget)}
          rescheduleAppointmentId={rescheduleTarget.id}
          category={category}
          org={{ id: category.organization }}
          userId={rescheduleTarget.user}
          token={token}
          onClose={() => setRescheduleTarget(null)}
          onSuccess={() => {
            // Keep the dialog open on its success screen (shows the new time); refresh the
            // list behind it so closing reveals the updated appointment.
            showToast('📅 Appointment rescheduled');
            fetchAppointments();
          }}
        />
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function CategoryPanel({ category, refreshKey = null }) {
  const [apptTab, setApptTab] = useState(0); // 0 = Unscheduled, 1 = Scheduled

  // If category explicitly indicates whether it's scheduled or not, render
  // only the appropriate appointment list (no tabs). This enforces the
  // rule that a category can only be one type.
  if (typeof category.is_scheduled === 'boolean') {
    return (
      <Box>
        {category.is_scheduled ? (
            <AppointmentList key={`${category.id}-scheduled`} category={category} apptType="scheduled" refreshKey={refreshKey} />
        ) : (
            <AppointmentList key={`${category.id}-unscheduled`} category={category} apptType="unscheduled" refreshKey={refreshKey} />
        )}
      </Box>
    );
  }

  // Fallback: if the category doesn't specify, keep the tabbed UI
  return (
    <Box>
      {/* Unscheduled / Scheduled sub-tabs */}
      <Tabs
        value={apptTab}
        onChange={(_, v) => setApptTab(v)}
        sx={{
          mb: 2.5,
          '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 40, py: 0.5 },
          '& .MuiTabs-indicator': {
              background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
              height: 3,
              borderRadius: 2,
            },
        }}
      >
        <Tab
          label="Walk-ins (Unscheduled)"
          icon={<DirectionsWalkIcon sx={{ fontSize: 17 }} />}
          iconPosition="start"
        />
        <Tab
          label="Scheduled"
          icon={<EventNoteIcon sx={{ fontSize: 17 }} />}
          iconPosition="start"
        />
      </Tabs>

        

      {apptTab === 0 && (
          <AppointmentList key={`${category.id}-unscheduled`} category={category} apptType="unscheduled" refreshKey={refreshKey} />
      )}
      {apptTab === 1 && (
          <AppointmentList key={`${category.id}-scheduled`} category={category} apptType="scheduled" refreshKey={refreshKey} />
      )}
    </Box>
  );
}

class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so developer can inspect stacktrace in browser
    // In production you may send this to an error-tracking service
    // eslint-disable-next-line no-console
    console.error('AdminPage render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>Something went wrong while rendering the admin page.</Alert>
          <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [statusUpdating, setStatusUpdating] = useState({});
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [organizations, setOrganizations] = useState([]);
  const [orgIndex, setOrgIndex] = useState(0);
  // The org the admin is currently viewing stays visible even if its categories drop to 0 (e.g.
  // after deleting the last queue) so they can still add a new one — otherwise the tab vanishes.
  const [pinnedOrgId, setPinnedOrgId] = useState(null);
  // Briefly highlights the card that was just reordered, so the move is visible.
  const [movedCatId, setMovedCatId] = useState(null);
  const [categoryTabIndexMap, setCategoryTabIndexMap] = useState({});
  // Queue modal / form state
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [queueModalCategory, setQueueModalCategory] = useState(null);
  const [queueForm, setQueueForm] = useState({ first_name: '', last_name: '', phone: '', email: '', notify: 'both' });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState('');
  // selected country for phone entry & per-field errors (default to India)
  const defaultCountry = detectDefaultCountry();
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);
  const [queueFormErrors, setQueueFormErrors] = useState({});

  // (Intentionally no effect that sets state synchronously.)
  // refreshKey increments to trigger appointment list refetch
  const [queueRefreshKey, setQueueRefreshKey] = useState(0);
  const [refreshCategoriesKey, setRefreshCategoriesKey] = useState(0);

  // Delete confirmation dialog state (replace native confirm)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Full account deletion (distinct from "remove access" above) — FK-safe on the backend.
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  // Create category dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const makeEmptyHours = () => {
    const o = {};
    DAYS_OF_WEEK.forEach((d) => { o[d] = []; });
    return o;
  };
  // Single source of truth for a blank create-category form. Previously this object literal was
  // duplicated in four places, so adding a field meant remembering all four.
  const emptyCreateForm = (overrides = {}) => ({
    name: '', description: '', type: 'general', is_scheduled: false,
    estimated_time: '', address_line1: '', address_line2: '', pincode: '', phone_number: '',
    // Walk-in wait-time settings. service_points=1 reproduces the previous serial maths.
    service_points: 1, wait_display_cap_minutes: 60, show_wait_time: true,
    opening_hours: makeEmptyHours(), break_hours: makeEmptyHours(),
    ...overrides,
  });
  const [createForm, setCreateForm] = useState(emptyCreateForm({ time_interval_per_appointment: 15 }));
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  // Refs for create form inputs so we can focus the first invalid field
  const createNameRef = useRef(null);
  const createDescriptionRef = useRef(null);
  const createEstimatedRef = useRef(null);
  const createPhoneRef = useRef(null);
  const createAddress1Ref = useRef(null);
  const createPincodeRef = useRef(null);

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateForm(emptyCreateForm());
    setCreateError('');
    setCreateFieldErrors({});
    setCreateLoading(false);
    setCreateDayErrors({});
  };

  // Minimal "create first category" flow, usable from the empty state (org has 0 categories,
  // so the main create dialog — which infers the org from the selected category — can't be used).
  const [firstCatOpen, setFirstCatOpen] = useState(false);
  const [firstCatName, setFirstCatName] = useState('');
  const [firstCatType, setFirstCatType] = useState('general');
  const [firstCatOrg, setFirstCatOrg] = useState('');
  const [firstCatOrgOptions, setFirstCatOrgOptions] = useState([]);
  const [firstCatBusy, setFirstCatBusy] = useState(false);
  const [firstCatError, setFirstCatError] = useState('');

  // Create Category Admin (org-scoped) dialog state
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ first_name: '', last_name: '', email: '', phone: '', category_ids: [] });
  // Optional login credentials for a NEW category admin (blank = OTP-only login).
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credConfirm, setCredConfirm] = useState('');
  const [credValid, setCredValid] = useState(true);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState('');
  const [createUserFieldErrors, setCreateUserFieldErrors] = useState({});
  // Refs for create/update category-admin user dialog
  const createUserFirstNameRef = useRef(null);
  const createUserEmailRef = useRef(null);
  const createUserPhoneRef = useRef(null);
  const createUserCategoryRef = useRef(null);
  const [categoryAdmins, setCategoryAdmins] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);

  const closeCreateUserDialog = () => {
    setCreateUserDialogOpen(false);
    setCreateUserForm({ first_name: '', last_name: '', email: '', phone: '', category_ids: [] });
    setCreateUserError('');
    setCreateUserLoading(false);
    setEditingUserId(null);
    setCreateUserFieldErrors({});
    setCredUsername(''); setCredPassword(''); setCredConfirm(''); setCredValid(true);
  };

  // Edit category dialog state
  const [editCatDialogOpen, setEditCatDialogOpen] = useState(false);
  const [editCatTarget, setEditCatTarget] = useState(null);
  const [editCatForm, setEditCatForm] = useState({ name: '', description: '', type: 'general', is_scheduled: false, time_interval_per_appointment: 15, estimated_time: '', service_points: 1, wait_display_cap_minutes: '', show_wait_time: true, address_line1: '', address_line2: '', pincode: '', phone_number: '', max_advance_days: 7, max_scheduled_per_user_per_day: '', time_zone: 'UTC', opening_hours: (function(){ const o={}; DAYS_OF_WEEK.forEach(d=>o[d]=[]); return o; })(), break_hours: (function(){ const o={}; DAYS_OF_WEEK.forEach(d=>o[d]=[]); return o; })() });
  const [editCatLoading, setEditCatLoading] = useState(false);
  const [editCatError, setEditCatError] = useState('');
  const [editFieldErrors, setEditFieldErrors] = useState({});
  // Refs for edit form inputs to focus on first invalid field after server validation
  const editNameRef = useRef(null);
  const editDescriptionRef = useRef(null);
  const editEstimatedRef = useRef(null);
  const editPhoneRef = useRef(null);
  const editAddress1Ref = useRef(null);
  const editPincodeRef = useRef(null);
  const editMaxAdvanceRef = useRef(null);
  const editMaxPerDayRef = useRef(null);
  const editTimezoneRef = useRef(null);

  // unscheduled counts per category (for queue size display)
  const [unscheduledCounts, setUnscheduledCounts] = useState({});
  // preview of queue shown in the Add-to-queue modal (count + first few users)
  const [queuePreview, setQueuePreview] = useState({ count: 0, items: [] });
  // per-day inline validation errors for create/edit forms
  const [createDayErrors, setCreateDayErrors] = useState({});
  const [editDayErrors, setEditDayErrors] = useState({});
  // available slot interval options (minutes) populated from DB (plus sensible defaults)
  const [intervalOptions, setIntervalOptions] = useState([10, 15, 30, 60, 90, 120]);

  // helpers for opening/break hours validation
  const timeToMinutes = (t) => {
    if (!t || typeof t !== 'string') return null;
    const parts = t.split(':');
    if (parts.length !== 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  // Parse various representations of the category interval into minutes.
  // Accepts number (minutes), strings like '15', '00:15:00' or '1:00:00', or null.
  const intervalToMinutes = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return Math.floor(v);
    if (typeof v === 'string') {
      // pure numeric string
      if (/^\d+$/.test(v)) return Number(v);
      // HH:MM:SS or H:MM:SS
      const parts = v.split(':').map((p) => p.replace(/^0+/, '') === '' ? '0' : p);
      if (parts.length >= 2) {
        const h = Number(parts[0]) || 0;
        const m = Number(parts[1]) || 0;
        if (!Number.isNaN(h) && !Number.isNaN(m)) return h * 60 + m;
      }
      // fallback: try parseInt
      const n = parseInt(v, 10);
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };

  // Convert minutes (integer) to a Django-compatible DurationField string 'HH:MM:SS'.
  // Django DurationField requires a duration string; sending a bare integer causes it
  // to be interpreted as seconds instead of minutes, resulting in 0-minute slots.
  const minutesToDuration = (mins) => {
    const m = Number(mins);
    if (!Number.isFinite(m) || m <= 0) return '00:15:00'; // safe default
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return `${String(h).padStart(2, '0')}:${String(rem).padStart(2, '0')}:00`;
  };

  const formatIntervalLabel = (m) => {
    if (!m && m !== 0) return '';
    if (m >= 60) {
      if (m % 60 === 0) {
        const h = m / 60;
        return h === 1 ? '1 hour' : `${h} hours`;
      }
      const h = Math.floor(m / 60);
      const rem = m % 60;
      return `${h}h ${rem}m`;
    }
    return `${m} minutes`;
  };

  const validateHours = (opening_hours, break_hours) => {
    // Return either null for no errors or an object { message, dayErrors }
    const dayErrors = {};
    for (const day of DAYS_OF_WEEK) {
      const opening = opening_hours[day] || [];
      if (!Array.isArray(opening)) {
        dayErrors[day] = `Opening hours for ${day} must be an array.`; continue;
      }
      if (opening.length === 0) continue; // closed is allowed
      if (opening.length !== 1) { dayErrors[day] = `Opening hours for ${day} must have one range.`; continue; }
      const [start, end] = opening[0] || [];
      const s = timeToMinutes(start);
      const e = timeToMinutes(end);
      if (s === null || e === null) { dayErrors[day] = `Invalid opening time format.`; continue; }
      if (s >= e) { dayErrors[day] = `Start must be earlier than end.`; continue; }

      const breaks = (break_hours && break_hours[day]) || [];
      for (const br of breaks) {
        const [bs, be] = br || [];
        const bsi = timeToMinutes(bs);
        const bei = timeToMinutes(be);
        if (bsi === null || bei === null) { dayErrors[day] = `Invalid break time format.`; break; }
        if (bsi >= bei) { dayErrors[day] = `Break start must be earlier than break end.`; break; }
        if (!(s <= bsi && bei <= e && bsi < bei)) { dayErrors[day] = `Break must be within opening hours.`; break; }
        if (bsi === s && bei === e) { dayErrors[day] = `Break cannot fully overlap opening hours.`; break; }
      }
    }
    const hasDays = Object.keys(dayErrors).length > 0;
    if (hasDays) return { message: 'Please fix the highlighted times.', dayErrors };
    return null;
  };

  // Helper: convert 'HH:MM' to 12-hour parts { display: 'h:mm', period: 'AM'|'PM' }
  const to12Hour = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return { display: '12:00', period: 'AM' };
    const parts = hhmm.split(':');
    if (parts.length !== 2) return { display: '12:00', period: 'AM' };
    let h = parseInt(parts[0], 10);
    const m = parts[1];
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    const display = `${h.toString().padStart(2, '0')}:${m}`;
    return { display, period };
  };

  // Helper: convert 12-hour display + period into 'HH:MM' 24-hour string
  const from12Hour = (display, period) => {
    if (!display || typeof display !== 'string') return '00:00';
    const parts = display.split(':');
    if (parts.length !== 2) return '00:00';
    let h = parseInt(parts[0], 10);
    const m = parts[1].padStart(2, '0');
    if (Number.isNaN(h)) h = 0;
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h = h + 12;
    }
    return `${h.toString().padStart(2, '0')}:${m}`;
  };

  // Render a nicer composite time input: 12-hour display + AM/PM select
  function TimeInput({ value24, onChange, label, sx = {} }) {
    // value24: 'HH:MM'
    const { display, period } = to12Hour(value24 || '09:00');
    const [hour, minute] = (display || '09:00').split(':');
    const [h, setH] = useState(hour.replace(/^0/, '') || '9');
    const [m, setM] = useState((minute && minute.padStart(2, '0')) || '00');
    const [per, setPer] = useState(period);

    useEffect(() => {
      const d = to12Hour(value24 || '09:00');
      const parts = (d.display || '09:00').split(':');
      setH(parts[0].replace(/^0/, ''));
      setM(parts[1]);
      setPer(d.period);
    }, [value24]);

    const hours = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
    const minuteOptions = ['00', '15', '30', '45'];

    const commit = (nh, nm, np) => {
      const disp = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      const next = from12Hour(disp, np);
      onChange && onChange(next);
    };

    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ...sx }}>
        <FormControl size="small" sx={{ minWidth: 56, '& .MuiSelect-select': { px: 1, py: 0.5, textAlign: 'center' }, '& .MuiOutlinedInput-notchedOutline': { borderRadius: 8 } }}>
          <Select value={String(h)} onChange={(e) => { const v = e.target.value; setH(v); commit(v, m, per); }}>
            {hours.map((hr) => <MenuItem key={hr} value={hr}>{hr}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 56, '& .MuiSelect-select': { px: 1, py: 0.5, textAlign: 'center' }, '& .MuiOutlinedInput-notchedOutline': { borderRadius: 8 } }}>
          <Select value={String(m)} onChange={(e) => { const v = e.target.value; setM(v); commit(h, v, per); }}>
            {minuteOptions.map((mm) => <MenuItem key={mm} value={mm}>{mm}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 64, '& .MuiSelect-select': { px: 1, py: 0.5, textAlign: 'center' }, '& .MuiOutlinedInput-notchedOutline': { borderRadius: 8 } }}>
          <Select value={per} onChange={(e) => { const v = e.target.value; setPer(v); commit(h, m, v); }}>
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }

  const toggleDayOpenCreate = (day, openFlag) => {
    setCreateForm((prev) => {
      const oh = { ...(prev.opening_hours || {}) };
      const bh = { ...(prev.break_hours || {}) };
      if (!openFlag) {
        oh[day] = [];
        bh[day] = [];
      } else {
        oh[day] = [['09:00', '17:00']];
        bh[day] = [];
      }
      return { ...prev, opening_hours: oh, break_hours: bh };
    });
    // clear any per-day errors for this day when toggling
    setCreateDayErrors((prev) => { const next = { ...(prev || {}) }; delete next[day]; return next; });
  };

  const toggleDayOpenEdit = (day, openFlag) => {
    setEditCatForm((prev) => {
      const oh = { ...(prev.opening_hours || {}) };
      const bh = { ...(prev.break_hours || {}) };
      if (!openFlag) {
        oh[day] = [];
        bh[day] = [];
      } else {
        oh[day] = [['09:00', '17:00']];
        bh[day] = [];
      }
      return { ...prev, opening_hours: oh, break_hours: bh };
    });
    setEditDayErrors((prev) => { const next = { ...(prev || {}) }; delete next[day]; return next; });
  };

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canManageOrg = useCallback((orgId) => {
    const isStaffFlag = localStorage.getItem('isStaff') === 'true';
    if (isStaffFlag) return true;
    const isOrgAdminFlag = localStorage.getItem('isOrgAdmin') === 'true';
    if (!isOrgAdminFlag) return false;
    try {
      const access = JSON.parse(localStorage.getItem('orgAccess') || '[]');
      return access && access.findIndex((x) => String(x) === String(orgId)) !== -1;
    } catch (e) {
      return false;
    }
  }, []);

  const token = localStorage.getItem('accessToken');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const isStaff = localStorage.getItem('isStaff') === 'true';
  const isSqipAdmin = localStorage.getItem('isSqipAdmin') === 'true';
  const isOrgAdmin = localStorage.getItem('isOrgAdmin') === 'true';
  // A user can hold BOTH roles (e.g. an org admin who added themselves as a category admin
  // using their own phone). When they do, show a toggle to switch between the org-admin
  // dashboard and the category/queue dashboard. Both roles already have backend access.
  const isCategoryAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('userGroups') || '[]').length > 0; } catch { return false; }
  })();
  const isBothRoles = isOrgAdmin && isCategoryAdmin;
  const [roleView, setRoleView] = useState(isOrgAdmin ? 'org' : 'category');
  // Booking-invite dialog target: { orgId, category }.
  const [inviteFor, setInviteFor] = useState(null);
  // Org-admin self-serve: also become a category admin so they can view/operate queues.
  const [grantingQueue, setGrantingQueue] = useState(false);
  const grantQueueAccess = async () => {
    setGrantingQueue(true);
    try {
      const access = JSON.parse(localStorage.getItem('orgAccess') || '[]');
      const orgId = access[0];
      if (orgId) {
        await fetch(`${API_BASE}/organizations/${orgId}/grant-queue-access/`,
          { method: 'POST', headers: authHeaders });
        // Refresh /me/ so the new group memberships land in localStorage, then reload the page
        // so isCategoryAdmin/roleView recompute and the queues become visible.
        try {
          const me = await fetch(`${API_BASE}/me/`, { headers: authHeaders }).then((r) => r.json());
          if (me && Array.isArray(me.groups)) localStorage.setItem('userGroups', JSON.stringify(me.groups));
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    window.location.reload();
  };

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError('');
      try {
        // Staff/superuser see all categories; group admins see only their categories
        const endpoint = isStaff
          ? `${API_BASE}/categories/?page_size=100`
          : `${API_BASE}/categories/user/?page_size=100`;
        const res = await fetch(endpoint, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          setCategories(data.results || []);
          // Also fetch organizations so we can group categories by org
          try {
            const orgRes = await fetch(`${API_BASE}/organizations/?page_size=100`, { headers: authHeaders });
            if (orgRes.ok) {
              const orgData = await orgRes.json();
              setOrganizations(orgData.results || []);
            }
          } catch (e) {
            // ignore org fetch errors; we'll fallback to org id labels
          }
          // If the user is an org-admin (no group membership) and categories were empty,
          // fetch org-scoped categories so org-admins see categories for their org_access.
          try {
            const isOrgAdmin = localStorage.getItem('isOrgAdmin') === 'true';
            if (isOrgAdmin) {
              const raw = localStorage.getItem('orgAccess') || '[]';
              const access = JSON.parse(raw);
              if (Array.isArray(access) && access.length > 0) {
                // Fetch categories for each org in access and append any missing ones
                for (const orgId of access) {
                  const orgCatRes = await fetch(`${API_BASE}/organizations/${orgId}/categories/`, { headers: authHeaders });
                  if (orgCatRes.ok) {
                    const orgCatData = await orgCatRes.json();
                    const orgCats = orgCatData.results || [];
                    // no-op for admins here; admins are fetched via the dedicated /admins/ endpoint per org
                    // Append categories that aren't already present (by id)
                    const existingIds = new Set((data.results || []).map((c) => c.id).concat((categories || []).map((c) => c.id)));
                    for (const c of orgCats) {
                      if (!existingIds.has(c.id)) {
                        setCategories((prev) => [...prev, c]);
                      }
                    }
                  }
                }
              }
            }
          } catch (e) {
            // ignore parsing/fetch errors
          }
        } else if (res.status === 401) {
          localStorage.clear();
          navigate('/');
        } else {
          setError('Failed to load categories.');
        }
      } catch {
        setError('Network error loading categories.');
      }
      setLoading(false);
    };
    fetchCategories();
  }, [token, isStaff, navigate]);

  // Re-fetch categories when refreshCategoriesKey increments
  useEffect(() => {
    // reuse the same fetch logic by toggling the dependency above -- simple approach: call fetch by forcing the outer effect
    // easiest: reload page data by calling window.dispatchEvent to trigger same fetch; to keep it local, just call the inner logic by reusing the effect
    // We'll trigger a small hack: call the same endpoint here to refresh organizations and categories when key changes.
    const doRefresh = async () => {
      try {
        const endpoint = isStaff
          ? `${API_BASE}/categories/?page_size=100`
          : `${API_BASE}/categories/user/?page_size=100`;
        const res = await fetch(endpoint, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          setCategories(data.results || []);
        }
        const orgRes = await fetch(`${API_BASE}/organizations/?page_size=100`, { headers: authHeaders });
        if (orgRes.ok) {
          const orgData = await orgRes.json();
          setOrganizations(orgData.results || []);
        }
        // For org-admins: also re-fetch the org-scoped categories so the list isn't wiped
        const isOrgAdminFlag = localStorage.getItem('isOrgAdmin') === 'true';
        if (isOrgAdminFlag) {
          const raw = localStorage.getItem('orgAccess') || '[]';
          let access = [];
          try { access = JSON.parse(raw); } catch (e) { /* ignore */ }
          if (Array.isArray(access) && access.length > 0) {
            const allOrgCats = [];
            for (const orgId of access) {
              const r = await fetch(`${API_BASE}/organizations/${orgId}/categories/`, { headers: authHeaders });
              if (r.ok) {
                const d = await r.json();
                (d.results || []).forEach((c) => allOrgCats.push(c));
              }
            }
            if (allOrgCats.length > 0) {
              // Merge with any already-fetched categories, deduplicating by id
              setCategories((prev) => {
                const byId = {};
                (prev || []).forEach((c) => { byId[c.id] = c; });
                allOrgCats.forEach((c) => { byId[c.id] = c; });
                return Object.values(byId);
              });
            }
          }
        }
      } catch (e) {
        // ignore
      }
    };
    if (refreshCategoriesKey > 0) doRefresh();
  }, [refreshCategoriesKey]);

  const handleToggleStatus = async (category) => {
    const newStatus = category.status === 'active' ? 'inactive' : 'active';
    setStatusUpdating((prev) => ({ ...prev, [category.id]: true }));
    try {
      const res = await fetch(`${API_BASE}/categories/${category.id}/update-status/`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setCategories((prev) =>
          prev.map((c) => (c.id === category.id ? { ...c, status: newStatus } : c))
        );
        setToast({ open: true, msg: `Category "${category.name || category.description}" set to ${newStatus}`, severity: 'success' });
      } else {
        const err = await res.json();
        setToast({ open: true, msg: err.detail || 'Failed to update status', severity: 'error' });
      }
    } catch {
      setToast({ open: true, msg: 'Network error', severity: 'error' });
    }
    setStatusUpdating((prev) => ({ ...prev, [category.id]: false }));
  };

  const openFirstCategory = () => {
    let access = [];
    try { access = JSON.parse(localStorage.getItem('orgAccess') || '[]').map(String); } catch (e) { access = []; }
    const src = isStaff ? organizations : organizations.filter((o) => access.includes(String(o.id)));
    const opts = src.length ? src.map((o) => ({ id: o.id, name: o.name })) : access.map((id) => ({ id, name: `Org #${id}` }));
    setFirstCatOrgOptions(opts);
    setFirstCatOrg(opts.length === 1 ? String(opts[0].id) : '');
    setFirstCatName(''); setFirstCatType('general'); setFirstCatError('');
    setFirstCatOpen(true);
  };

  const submitFirstCategory = async () => {
    if (!firstCatName.trim()) { setFirstCatError('Category name is required.'); return; }
    if (!firstCatOrg) { setFirstCatError('Please choose an organization.'); return; }
    setFirstCatBusy(true); setFirstCatError('');
    try {
      const res = await fetch(`${API_BASE}/categories/`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ organization: Number(firstCatOrg), name: firstCatName.trim(), type: firstCatType, status: 'active' }),
      });
      if (res.ok) {
        setFirstCatOpen(false);
        setToast({ open: true, msg: 'Category created', severity: 'success' });
        setRefreshCategoriesKey((k) => k + 1);
      } else {
        const err = await res.json().catch(() => ({}));
        setFirstCatError(parseApiError(err, 'Failed to create category'));
      }
    } catch (e) {
      setFirstCatError('Network error');
    }
    setFirstCatBusy(false);
  };

  const handleCreateCategory = async () => {
    setCreateError('');
    setCreateLoading(true);
    try {
      // validate scheduled hours if enabled
      if (createForm.is_scheduled) {
        const v = validateHours(createForm.opening_hours || {}, createForm.break_hours || {});
        if (v) {
          if (v.dayErrors) setCreateDayErrors(v.dayErrors);
          setCreateError(v.message || v);
          setCreateLoading(false);
          return;
        } else {
          setCreateDayErrors({});
        }
      }
      const payload = {
        ...createForm,
        organization: currentOrg && currentOrg.id,
        // Convert minutes integer to HH:MM:SS duration string for Django DurationField
        time_interval_per_appointment: minutesToDuration(createForm.time_interval_per_appointment),
        // Number inputs hand back strings, and a cleared field gives ''. Django rejects '' for an
        // integer column, so normalise: blank cap means "always show the exact estimate" (null).
        service_points: Number(createForm.service_points) || 1,
        wait_display_cap_minutes: createForm.wait_display_cap_minutes === '' || createForm.wait_display_cap_minutes == null
          ? null : Number(createForm.wait_display_cap_minutes),
        estimated_time: createForm.estimated_time === '' ? null : Number(createForm.estimated_time),
      };
      const res = await fetch(`${API_BASE}/categories/`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setCreateDialogOpen(false);
  setCreateForm(emptyCreateForm());
        setCreateDayErrors({});
        setToast({ open: true, msg: 'Category created', severity: 'success' });
        setRefreshCategoriesKey((k) => k + 1);
      } else {
          const err = await res.json().catch(() => ({}));
          console.error('Create category failed', res.status, err); // eslint-disable-line no-console
          // If the API returned field-level validation errors, attach them to the form
          // Top-level error keys (detail/code/limit/message/status) are NOT form fields — treating
          // them as field errors is what mishandled the "category limit reached" response.
          const META = new Set(['detail', 'code', 'limit', 'message', 'status']);
          const fieldErrs = {};
          if (err && typeof err === 'object') {
            const src = err.errors && typeof err.errors === 'object' ? err.errors : err;
            for (const [k, v] of Object.entries(src)) {
              if (typeof k === 'string' && !META.has(k)) {
                const msg = Array.isArray(v) ? v.join(' ') : String(v);
                fieldErrs[k] = msg;
              }
            }
          }
          if (Object.keys(fieldErrs).length > 0) {
            setCreateFieldErrors(fieldErrs);
            setCreateError('Please correct the highlighted fields.');
            // Focus the first invalid create field (in a predictable order)
            (function focusFirstCreateField() {
              const order = ['name', 'description', 'estimated_time', 'address_line1', 'pincode', 'phone_number'];
              for (const k of order) {
                if (fieldErrs[k]) {
                  try {
                    if (k === 'name' && createNameRef.current) { createNameRef.current.focus(); return; }
                    if (k === 'description' && createDescriptionRef.current) { createDescriptionRef.current.focus(); return; }
                    if (k === 'estimated_time' && createEstimatedRef.current) { createEstimatedRef.current.focus(); return; }
                    if (k === 'address_line1' && createAddress1Ref.current) { createAddress1Ref.current.focus(); return; }
                    if (k === 'pincode' && createPincodeRef.current) { createPincodeRef.current.focus(); return; }
                    if (k === 'phone_number' && createPhoneRef.current) { createPhoneRef.current.focus(); return; }
                  } catch (_) { /* ignore focus failures */ }
                }
              }
            })();
          } else {
            // General error (e.g. category limit reached) — show the message cleanly.
            setCreateError(err.detail || err.message || parseApiError(err, 'Failed to create category'));
          }
      }
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Create category exception', e);
        setCreateError('Network error');
    }
    setCreateLoading(false);
  };

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE}/categories/${deleteTarget.id}/`, { method: 'DELETE', headers: authHeaders });
      const body = res.status === 204 ? {} : await res.json().catch(() => ({}));
      if (res.ok || res.status === 204) {
        // Backend may hard-delete (204) or archive (200 + status:'archived') if it has history.
        const msg = body.status === 'archived'
          ? 'Category has history, so it was archived (hidden) instead of deleted'
          : 'Category deleted';
        setToast({ open: true, msg, severity: 'success' });
        setDeleteConfirmOpen(false);
        setDeleteTarget(null);
        setRefreshCategoriesKey((k) => k + 1);
      } else if (res.status === 409) {
        // Live queue — people still waiting/being served. Keep the dialog open so they can act.
        setToast({ open: true, msg: body.detail || 'This queue still has people in it.', severity: 'warning' });
      } else {
        setToast({ open: true, msg: body.detail || 'Failed to delete category', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, msg: 'Network error', severity: 'error' });
    }
  };

  const handleOpenEditCat = (cat) => {
    setEditCatTarget(cat);
    setEditCatForm({
      name: cat.name || '',
      description: cat.description || '',
      type: cat.type || 'general',
      is_scheduled: !!cat.is_scheduled,
      time_interval_per_appointment: (intervalToMinutes(cat.time_interval_per_appointment) || 15),
      estimated_time: cat.estimated_time != null ? String(cat.estimated_time) : '',
      service_points: cat.service_points != null ? cat.service_points : 1,
      wait_display_cap_minutes: cat.wait_display_cap_minutes != null ? String(cat.wait_display_cap_minutes) : '',
      show_wait_time: cat.show_wait_time !== false,
      address_line1: cat.address_line1 || '',
      address_line2: cat.address_line2 || '',
      pincode: cat.pincode || '',
      phone_number: cat.phone_number || '',
      max_advance_days: cat.max_advance_days != null ? cat.max_advance_days : 7,
      max_scheduled_per_user_per_day: cat.max_scheduled_per_user_per_day != null ? String(cat.max_scheduled_per_user_per_day) : '',
      time_zone: cat.time_zone || 'UTC',
      opening_hours: cat.opening_hours || (function(){ const o={}; DAYS_OF_WEEK.forEach(d=>o[d]=[]); return o; })(),
      break_hours: cat.break_hours || (function(){ const o={}; DAYS_OF_WEEK.forEach(d=>o[d]=[]); return o; })(),
      notify_queue_positions: Array.isArray(cat.notify_queue_positions) ? cat.notify_queue_positions.join(', ') : '',
    });
    setEditCatError('');
    setEditFieldErrors({});
    setEditDayErrors({});
    setEditCatDialogOpen(true);
  };

  const handleEditCategory = async () => {
    if (!editCatTarget) return;
    setEditCatLoading(true);
    setEditCatError('');
    // validate scheduled hours if enabled
    if (editCatForm.is_scheduled) {
      const v = validateHours(editCatForm.opening_hours || {}, editCatForm.break_hours || {});
      if (v) {
        if (v.dayErrors) setEditDayErrors(v.dayErrors);
        setEditCatError(v.message || v);
        setEditCatLoading(false);
        return;
      } else {
        setEditDayErrors({});
      }
    }
    try {
      const editPayload = {
        ...editCatForm,
        // Convert minutes integer to HH:MM:SS duration string for Django DurationField
        time_interval_per_appointment: minutesToDuration(editCatForm.time_interval_per_appointment),
        // Cast numeric fields — send null if blank
        max_advance_days: editCatForm.max_advance_days !== '' ? Number(editCatForm.max_advance_days) : 7,
        max_scheduled_per_user_per_day: editCatForm.max_scheduled_per_user_per_day !== '' ? Number(editCatForm.max_scheduled_per_user_per_day) : null,
        estimated_time: editCatForm.estimated_time !== '' ? Number(editCatForm.estimated_time) : null,
        service_points: Number(editCatForm.service_points) || 1,
        wait_display_cap_minutes: editCatForm.wait_display_cap_minutes !== '' ? Number(editCatForm.wait_display_cap_minutes) : null,
        show_wait_time: !!editCatForm.show_wait_time,
        notify_queue_positions: String(editCatForm.notify_queue_positions || '').split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isInteger(n) && n >= 1),
      };
      const res = await fetch(`${API_BASE}/categories/${editCatTarget.id}/update-info/`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(editPayload),
      });
      if (res.ok) {
        const updated = await res.json().catch(() => null);
        // Update the category in local state directly — avoids triggering a full re-fetch
        // which would wipe org-admin categories (they come from /organizations/{id}/categories/).
        setCategories((prev) =>
          prev.map((c) => {
            if (c.id !== editCatTarget.id) return c;
            // Merge the returned data with local edits as fallback
            return updated ? { ...c, ...updated } : { ...c, ...editCatForm };
          })
        );
        setEditCatDialogOpen(false);
        setEditCatTarget(null);
        setEditDayErrors({});
        setToast({ open: true, msg: 'Category updated', severity: 'success' });
      } else {
        const err = await res.json().catch(() => ({}));
        // eslint-disable-next-line no-console
        console.error('Edit category failed', res.status, err); // eslint-disable-line no-console
        // Handle field-level validation errors specially so fields can show helperText
        const fieldErrs = {};
        if (err && typeof err === 'object') {
          const src = err.errors && typeof err.errors === 'object' ? err.errors : err;
          for (const [k, v] of Object.entries(src)) {
            if (typeof k === 'string') {
              const msg = Array.isArray(v) ? v.join(' ') : String(v);
              fieldErrs[k] = msg;
            }
          }
        }
        if (Object.keys(fieldErrs).length > 0) {
          setEditFieldErrors(fieldErrs);
          setEditCatError('Please correct the highlighted fields.');
          // Focus the first invalid edit field
          (function focusFirstEditField() {
            const order = ['name', 'description', 'estimated_time', 'phone_number', 'address_line1', 'pincode', 'max_advance_days', 'max_scheduled_per_user_per_day', 'time_zone'];
            for (const k of order) {
              if (fieldErrs[k]) {
                try {
                  if (k === 'name' && editNameRef.current) { editNameRef.current.focus(); return; }
                  if (k === 'description' && editDescriptionRef.current) { editDescriptionRef.current.focus(); return; }
                  if (k === 'estimated_time' && editEstimatedRef.current) { editEstimatedRef.current.focus(); return; }
                  if (k === 'phone_number' && editPhoneRef.current) { editPhoneRef.current.focus(); return; }
                  if (k === 'address_line1' && editAddress1Ref.current) { editAddress1Ref.current.focus(); return; }
                  if (k === 'pincode' && editPincodeRef.current) { editPincodeRef.current.focus(); return; }
                  if (k === 'max_advance_days' && editMaxAdvanceRef.current) { editMaxAdvanceRef.current.focus(); return; }
                  if (k === 'max_scheduled_per_user_per_day' && editMaxPerDayRef.current) { editMaxPerDayRef.current.focus(); return; }
                  if (k === 'time_zone' && editTimezoneRef.current) { editTimezoneRef.current.focus(); return; }
                } catch (_) { /* ignore focus failures */ }
              }
            }
          })();
        } else {
          setEditCatError(parseApiError(err, 'Failed to update category'));
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Edit category exception', e);
      setEditCatError('Network error');
    }
    setEditCatLoading(false);
  };

  // Create a category-scoped admin user for the current organization
  const handleCreateCategoryUser = async () => {
    setCreateUserError('');
    setCreateUserFieldErrors({});
    setCreateUserLoading(true);
    try {
      // basic validation
      const errs = {};
  if (!createUserForm.first_name || createUserForm.first_name.trim().length === 0) errs.first_name = 'First name required';
  // phone is optional for admin users
  if (!Array.isArray(createUserForm.category_ids) || createUserForm.category_ids.length === 0) errs.category_ids = 'Select at least one category';
      if (Object.keys(errs).length > 0) {
        setCreateUserError(Object.values(errs).join(' | '));
        setCreateUserLoading(false);
        return;
      }

      // Optional credentials: both-or-neither, and must pass the field validation.
      if (!editingUserId && (credUsername.trim() || credPassword)) {
        if (!credUsername.trim() || !credPassword) {
          setCreateUserError('Provide both a username and a password, or leave both blank.');
          setCreateUserLoading(false);
          return;
        }
        if (!credValid) {
          setCreateUserError('Fix the login credentials (username availability / password rules) first.');
          setCreateUserLoading(false);
          return;
        }
      }

      // normalize phone only if provided (phone is optional)
      let payload = {
        first_name: createUserForm.first_name.trim(),
        last_name: createUserForm.last_name ? createUserForm.last_name.trim() : '',
        email: createUserForm.email ? createUserForm.email.trim() : '',
        category_ids: createUserForm.category_ids,
      };
      if (!editingUserId && credUsername.trim() && credPassword) {
        payload.username = credUsername.trim();
        payload.password = credPassword;
      }
      if (createUserForm.phone && String(createUserForm.phone).trim().length > 0) {
        let phone = (createUserForm.phone || '').trim();
        if (!phone.startsWith('+')) {
          // try to prefix with default country dial
          const dial = selectedCountry && selectedCountry.dial ? selectedCountry.dial : COUNTRIES[0].dial;
          const digits = phone.replace(/\D/g, '');
          phone = `${dial}${digits}`;
          if (!phone.startsWith('+')) phone = `+${phone.replace(/^\+?/, '')}`;
        }
        payload.phone = phone;
      }

      // Organization id must be present
      const orgId = currentOrg && currentOrg.id;
      if (!orgId) {
        setCreateUserError('Organization not selected');
        setCreateUserLoading(false);
        return;
      }

      // If editingUserId set, call update endpoint
      let endpoint = `${API_BASE}/organizations/${orgId}/create-category-user/`;
      if (editingUserId) {
        endpoint = `${API_BASE}/organizations/${orgId}/update-category-user/`;
      }

      const body = editingUserId ? { ...payload, user_id: editingUserId } : payload;
      const res = await fetch(endpoint, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(body),
      });
      if (res.ok) {
        closeCreateUserDialog();
        setToast({ open: true, msg: editingUserId ? 'Category admin updated' : 'Category admin created', severity: 'success' });
        setRefreshCategoriesKey((k) => k + 1);
        // refresh admins list from dedicated endpoint
        try {
          const ares = await fetch(`${API_BASE}/organizations/${orgId}/admins/`, { headers: authHeaders });
          if (ares.ok) {
            const adata = await ares.json();
            if (adata.admins) setCategoryAdmins(adata.admins || []);
          }
        } catch (e) {
          // ignore
        }
      } else {
        const err = await res.json().catch(() => ({}));
        // eslint-disable-next-line no-console
        console.error('Create/update category user failed', res.status, err); // eslint-disable-line no-console
        // Check for field-level validation errors
        const fieldErrs = {};
        if (err && typeof err === 'object') {
          const src = err.errors && typeof err.errors === 'object' ? err.errors : err;
          for (const [k, v] of Object.entries(src)) {
            if (typeof k === 'string') {
              const msg = Array.isArray(v) ? v.join(' ') : String(v);
              fieldErrs[k] = msg;
            }
          }
        }
        if (Object.keys(fieldErrs).length > 0) {
          setCreateUserFieldErrors(fieldErrs);
          setCreateUserError('Please correct the highlighted fields.');
          // Focus first invalid create-user field
          (function focusFirstCreateUserField() {
            const order = ['first_name', 'email', 'phone', 'category_ids'];
            for (const k of order) {
              if (fieldErrs[k]) {
                try {
                  if (k === 'first_name' && createUserFirstNameRef.current) { createUserFirstNameRef.current.focus(); return; }
                  if (k === 'email' && createUserEmailRef.current) { createUserEmailRef.current.focus(); return; }
                  if (k === 'phone' && createUserPhoneRef.current) { createUserPhoneRef.current.focus(); return; }
                  if (k === 'category_ids' && createUserCategoryRef.current) { if (createUserCategoryRef.current.focus) createUserCategoryRef.current.focus(); return; }
                } catch (_) { /* ignore */ }
              }
            }
          })();
        } else {
          setCreateUserError(parseApiError(err, 'Failed to create/update user'));
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Create category user exception', e);
      setCreateUserError('Network error');
    }
    setCreateUserLoading(false);
  };

  const handleOpenEditUser = (user) => {
    try {
      // prefer to split stored E.164 phone into country + local number for editing
      // Try several possible phone fields returned by the API (be defensive)
      let phoneRaw = '';
      if (user) {
        if (user.phone) phoneRaw = user.phone;
        else if (user.phone_number) phoneRaw = user.phone_number;
        else {
          // fallback: pick any key that contains 'phone'
          for (const k of Object.keys(user)) {
            if (k.toLowerCase().includes('phone') && user[k]) {
              phoneRaw = user[k];
              break;
            }
          }
        }
      }
      let digits = (phoneRaw + '').replace(/\D/g, '');
      let matched = null;
      for (const c of COUNTRIES) {
        const cd = (c.dial || '').replace(/\D/g, '');
        if (cd && digits.startsWith(cd)) {
          matched = { country: c, local: digits.slice(cd.length) };
          break;
        }
      }
      if (matched) {
        setSelectedCountry(matched.country);
        setCreateUserForm({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '', phone: matched.local || '', category_ids: user.category_ids || [] });
      } else {
        // fallback: keep full digits in phone field
        setCreateUserForm({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '', phone: phoneRaw || '', category_ids: user.category_ids || [] });
      }
      setEditingUserId(user.id);
      setCreateUserDialogOpen(true);
    } catch (e) {
      // defensive fallback
      setEditingUserId(user.id);
      setCreateUserForm({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '', phone: user.phone || '', category_ids: user.category_ids || [] });
      setCreateUserDialogOpen(true);
    }
  };

  const handleDeleteCategoryUser = async (userId) => {
    // Open app-styled confirmation dialog instead of native confirm()
    if (!userId) return;
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const performDeleteCategoryUser = async () => {
    const userId = userToDelete;
    if (!userId) return;
    const orgId = currentOrg && currentOrg.id;
    if (!orgId) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE}/organizations/${orgId}/delete-category-user/`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        setToast({ open: true, msg: 'Admin access removed', severity: 'success' });
        setRefreshCategoriesKey((k) => k + 1);
        // refresh admins
        try {
          const ares = await fetch(`${API_BASE}/organizations/${orgId}/admins/`, { headers: authHeaders });
          if (ares.ok) {
            const adata = await ares.json();
            if (adata.admins) setCategoryAdmins(adata.admins || []);
          }
        } catch (e) {
          // ignore admin refresh errors
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setToast({ open: true, msg: err.detail || 'Failed to delete user', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, msg: 'Network error', severity: 'error' });
    }
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Permanently delete a user's account (distinct from "remove access"). The backend hard-deletes
  // when possible and falls back to deactivating the account if protected history blocks it.
  const handleDeleteUserAccount = (userId) => {
    if (!userId) return;
    setAccountToDelete(userId);
    setDeleteAccountDialogOpen(true);
  };

  const performDeleteUserAccount = async () => {
    const userId = accountToDelete;
    const orgId = currentOrg && currentOrg.id;
    if (!userId || !orgId) return;
    setDeleteAccountLoading(true);
    try {
      const res = await fetch(`${API_BASE}/organizations/${orgId}/delete-category-user-account/`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ user_id: userId }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const msg = body.status === 'deactivated'
          ? 'User had history — account deactivated and access removed'
          : 'User account deleted';
        setToast({ open: true, msg, severity: 'success' });
        setCreateUserDialogOpen(false);
        setEditingUserId(null);
        setRefreshCategoriesKey((k) => k + 1);
        try {
          const ares = await fetch(`${API_BASE}/organizations/${orgId}/admins/`, { headers: authHeaders });
          if (ares.ok) {
            const adata = await ares.json();
            if (adata.admins) setCategoryAdmins(adata.admins || []);
          }
        } catch (e) { /* ignore admin refresh errors */ }
      } else {
        setToast({ open: true, msg: body.detail || 'Failed to delete account', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, msg: 'Network error', severity: 'error' });
    }
    setDeleteAccountLoading(false);
    setDeleteAccountDialogOpen(false);
    setAccountToDelete(null);
  };

  // Set a category's display position (1-based). Moves it within the org's list and persists the
  // full order; the public /<slug>/ page and admin views both read this sort_order.
  const handleReorderCategory = async (cat, newPos) => {
    const orgId = currentOrg && currentOrg.id;
    if (!orgId) return;
    const order = computeReorder((currentOrg && currentOrg.categories) || [], cat.id, newPos);
    if (!order) return;
    // Optimistically stamp the new sort_order onto local state so the cards re-sort immediately
    // (groupedOrgs sorts by sort_order). The backend assigns the same positions.
    const posMap = new Map(order.map((id, i) => [id, i]));
    setCategories((prev) => prev.map((c) =>
      posMap.has(c.id) ? { ...c, sort_order: posMap.get(c.id) } : c));
    try {
      const res = await fetch(`${API_BASE}/organizations/${orgId}/reorder-categories/`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ order }),
      });
      if (res.ok) {
        setToast({ open: true, msg: `Moved "${cat.name || cat.description || 'queue'}" to #${Number(newPos)}`, severity: 'success' });
        setMovedCatId(cat.id);
        setTimeout(() => setMovedCatId((id) => (id === cat.id ? null : id)), 1600);
      } else {
        setToast({ open: true, msg: 'Failed to update order', severity: 'error' });
        setRefreshCategoriesKey((k) => k + 1);  // revert optimistic order from the server
      }
    } catch (e) {
      setToast({ open: true, msg: 'Network error updating order', severity: 'error' });
      setRefreshCategoriesKey((k) => k + 1);
    }
  };

  // Queue modal handlers
  const closeQueueModal = () => {
    setQueueModalOpen(false);
    setQueueError('');
  };

  const submitQueueForm = async () => {
    if (!queueModalCategory) return;
    setQueueLoading(true);
    setQueueError('');
    setQueueFormErrors({});

    // Basic front-end validation
    const errors = {};
    if (!queueForm.first_name || queueForm.first_name.trim().length === 0) {
      errors.first_name = 'First name is required';
    }
    if (!queueForm.phone || queueForm.phone.trim().length === 0) {
      errors.phone = 'Phone is required';
    } else {
      // strip non-digits for length check but preserve leading + for full value later
      const digits = queueForm.phone.replace(/\D/g, '');
      if (digits.length < 6) errors.phone = 'Enter a valid phone number';
    }

    if (Object.keys(errors).length > 0) {
      setQueueFormErrors(errors);
      setQueueLoading(false);
      return;
    }

    // Normalize phone: ensure it starts with +countrycode
    let phoneRaw = queueForm.phone.trim();
    // If user supplied a leading +, keep it, otherwise prepend selected country's dial code
    let phoneDigits = phoneRaw.replace(/[^\d+]/g, '');
    if (!phoneDigits.startsWith('+')) {
      const dial = selectedCountry && selectedCountry.dial ? selectedCountry.dial : COUNTRIES[0].dial;
      // remove leading zeros or plus signs from phoneDigits
      const justDigits = phoneDigits.replace(/\D/g, '');
      phoneDigits = `${dial}${justDigits.startsWith('0') ? justDigits.replace(/^0+/, '') : justDigits}`;
      if (!phoneDigits.startsWith('+')) phoneDigits = `+${phoneDigits.replace(/^\+?/, '')}`;
    }

    try {
      const payload = {
        organization: queueModalCategory.organization || queueModalCategory.organization_id || queueModalCategory.org_id || queueModalCategory.organization,
        category: queueModalCategory.id,
        first_name: queueForm.first_name.trim(),
        last_name: queueForm.last_name ? queueForm.last_name.trim() : '',
        phone: phoneDigits,
        country_code: selectedCountry ? selectedCountry.code : 'CA',
        email: queueForm.email ? queueForm.email.trim() : '',
        notify: queueForm.notify || 'both',
      };
      const res = await fetch(`${API_BASE}/appointments/add_user_to_queue/`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        // success
        setToast({ open: true, msg: 'User added to queue', severity: 'success' });
        // bump refresh key to force lists to reload
        setQueueRefreshKey((k) => k + 1);
        closeQueueModal();
      } else {
        try {
          const err = await res.json();
          setQueueError(parseApiError(err, 'Failed to add user to queue'));
        } catch (e) {
          setQueueError('Failed to add user to queue');
        }
      }
    } catch (e) {
      setQueueError('Network error');
    }
    setQueueLoading(false);
  };

  // Build organizations grouped with their categories for hierarchical tabs
  // Orgs this user explicitly administers. A newly onboarded org has no categories yet, so it
  // must still be listed (otherwise its admin can never create the first one).
  const [orgAccessIds, setOrgAccessIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('orgAccess') || '[]').map(String));
    } catch (e) {
      return new Set();
    }
  });

  // org_access is only written to localStorage at login, so an organization provisioned since
  // then would never appear. Refresh it from /me/ on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/me/`, { headers: authHeaders });
        if (!res.ok) return;
        const me = await res.json();
        if (!alive || !Array.isArray(me.org_access)) return;
        localStorage.setItem('orgAccess', JSON.stringify(me.org_access));
        setOrgAccessIds(new Set(me.org_access.map(String)));
      } catch (e) { /* non-fatal: fall back to the cached list */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedOrgs = React.useMemo(() => {
    const map = {};
    organizations.forEach((o) => { map[o.id] = { ...o, categories: [] }; });
    const unassigned = { id: null, name: 'Unassigned', categories: [] };
    categories.forEach((c) => {
      if (c.organization && map[c.organization]) {
        map[c.organization].categories.push(c);
      } else {
        unassigned.categories.push(c);
      }
    });
    // Display order is authoritative on sort_order (the admin-set display order), then id as a
    // stable tiebreak — NOT the flat array order, which varies by fetch path (staff list vs the
    // per-org merge that comes back in numeric-id order).
    Object.values(map).forEach((o) => { o.categories = sortCategoriesByOrder(o.categories); });
    // Include organizations that have categories available to the user, plus any org the user
    // administers directly (which may legitimately have zero categories).
    const arr = Object.values(map).filter((o) => (
      (Array.isArray(o.categories) && o.categories.length > 0)
      || orgAccessIds.has(String(o.id))
      || String(o.id) === String(pinnedOrgId)  // keep the org you're viewing, even at 0 categories
    ));
    if (unassigned.categories.length) arr.push(unassigned);
    return arr;
  }, [organizations, categories, orgAccessIds, pinnedOrgId]);

  const currentOrg = groupedOrgs[orgIndex] || groupedOrgs[0] || null;
  // Keep `pinnedOrgId` tracking the org currently in view so it stays listed if its last category
  // is deleted. Switching to another org re-pins there, letting a stale empty org drop off.
  React.useEffect(() => {
    if (currentOrg && currentOrg.id != null && String(currentOrg.id) !== String(pinnedOrgId)) {
      setPinnedOrgId(String(currentOrg.id));
    }
  }, [currentOrg, pinnedOrgId]);
  const currentCategory = currentOrg
    ? (currentOrg.categories[(categoryTabIndexMap[currentOrg.id] || 0)] || null)
    : null;

  // Fetch admins for the selected organization using the dedicated endpoint.
  useEffect(() => {
    let mounted = true;
    const fetchAdmins = async (orgId) => {
      try {
        const res = await fetch(`${API_BASE}/organizations/${orgId}/admins/`, { headers: authHeaders });
        if (!mounted) return;
        if (res.ok) {
          const data = await res.json();
          setCategoryAdmins(data.admins || []);
        } else {
          setCategoryAdmins([]);
        }
      } catch (e) {
        if (!mounted) return;
        setCategoryAdmins([]);
      }
    };

    if (currentOrg && currentOrg.id) {
      fetchAdmins(currentOrg.id);
    }

    return () => { mounted = false; };
  }, [currentOrg, refreshCategoriesKey]);

  // Populate slot-interval options from DB (categories on the selected org), keep sensible defaults
  useEffect(() => {
    const defaults = [10, 15, 30, 60, 90, 120];
    if (!currentOrg) {
      setIntervalOptions(defaults);
      return;
    }
    const s = new Set(defaults);
    (currentOrg.categories || []).forEach((c) => {
      const mins = intervalToMinutes(c && c.time_interval_per_appointment);
      if (mins && Number.isFinite(mins)) s.add(mins);
    });
    const arr = Array.from(s).sort((a, b) => a - b);
    setIntervalOptions(arr);
  }, [currentOrg]);

  // Fetch unscheduled queue counts for visible categories so we can show 'N in queue'
  useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const map = {};
        for (const c of categories || []) {
          if (c && !c.is_scheduled) {
            try {
              const r = await fetch(`${API_BASE}/appointments/unscheduled-count/?category_id=${c.id}&status=active`, { headers: authHeaders });
              if (r.ok) {
                const d = await r.json();
                map[c.id] = d.count || 0;
              }
            } catch (e) {
              // ignore
            }
          }
        }
        if (mounted) setUnscheduledCounts((prev) => ({ ...prev, ...map }));
      } catch (e) {
        // ignore
      }
    };
    if (categories && categories.length > 0) fetchCounts();
    return () => { mounted = false; };
  }, [categories]);

  // When the Add-to-queue modal opens for a category, fetch a small preview
  // of the queue (count + first few users) so we can show "N people in front".
  useEffect(() => {
    let mounted = true;
    const fetchPreview = async () => {
      if (!queueModalOpen || !queueModalCategory) return;
        try {
        const r = await fetch(`${API_BASE}/appointments/unscheduled-count/?category_id=${queueModalCategory.id}&status=active`, { headers: authHeaders });
        if (!mounted) return;
        if (r.ok) {
          const d = await r.json();
          setQueuePreview({ count: d.count || 0, items: [] });
        } else {
          setQueuePreview({ count: 0, items: [] });
        }
      } catch (e) {
        if (!mounted) return;
        setQueuePreview({ count: 0, items: [] });
      }
    };
    fetchPreview();
    return () => { mounted = false; };
  }, [queueModalOpen, queueModalCategory]);

  // If the logged-in user is an org-admin, default the org tab to the first org in their
  // org_access. This runs ONCE: re-applying it on every orgIndex change would immediately
  // snap the user back to the first org and make the other tabs unclickable.
  const orgIndexDefaulted = React.useRef(false);
  useEffect(() => {
    if (orgIndexDefaulted.current) return;
    try {
      const isOrgAdminFlag = localStorage.getItem('isOrgAdmin') === 'true';
      if (!isOrgAdminFlag) return;
      const access = JSON.parse(localStorage.getItem('orgAccess') || '[]');
      if (!Array.isArray(access) || access.length === 0) return;
      if (!groupedOrgs || groupedOrgs.length === 0) return;
      // Normalize ids to strings for robust comparison
      const accessSet = new Set(access.map((v) => String(v)));
      const idx = groupedOrgs.findIndex((o) => o.id !== null && accessSet.has(String(o.id)));
      if (idx === -1) return;
      orgIndexDefaulted.current = true;
      if (idx !== orgIndex) {
        // Defer setState so the effect finishes before the re-render it triggers.
        const t = setTimeout(() => setOrgIndex(idx), 0);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // ignore JSON parse errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedOrgs]);

  return (
    <AdminErrorBoundary>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        {/* Header */}
        <Fade in timeout={400}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 32, color: (theme) => theme.palette.custom ? theme.palette.custom.teal : '#00C4CC' }} />
                <Typography variant="h4" fontWeight={900} sx={{ color: 'text.primary' }}>Admin Dashboard</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Manage queues, check-ins, and category status across your organization.
              </Typography>
            </Box>
            {(localStorage.getItem('isOrgAdmin') === 'true' || localStorage.getItem('isStaff') === 'true') && (
              <Button
                variant="outlined"
                startIcon={<ReceiptLongOutlinedIcon />}
                onClick={() => navigate('/admin/billing')}
                sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
              >
                Billing &amp; Insights
              </Button>
            )}
          </Box>
        </Fade>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        {loading && (
          <Box>
            <Skeleton variant="rounded" height={48} sx={{ mb: 2, borderRadius: 3 }} />
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1, borderRadius: 3 }} />
            ))}
          </Box>
        )}

        {!loading && groupedOrgs.length === 0 && !error && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 72, opacity: 0.15, mb: 2, color: (theme) => theme.palette.custom ? theme.palette.custom.teal : '#00C4CC' }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {(isStaff || isOrgAdmin) ? 'No categories yet' : 'No categories assigned'}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {(isStaff || isOrgAdmin)
                ? 'Create your first category to start managing queues and appointments.'
                : 'You have no categories to manage. Ask a super admin to assign groups to your account.'}
            </Typography>
            {(isStaff || isOrgAdmin) && (
              <Button variant="contained" onClick={openFirstCategory}>Create category</Button>
            )}
          </Box>
        )}

        {/* Minimal create-category dialog — works from the empty state (no existing category) */}
        <Dialog open={firstCatOpen} onClose={() => !firstCatBusy && setFirstCatOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Create category</DialogTitle>
          <DialogContent>
            {firstCatError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{firstCatError}</Alert>}
            {firstCatOrgOptions.length !== 1 && (
              <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
                <InputLabel id="fc-org">Organization</InputLabel>
                <Select labelId="fc-org" label="Organization" value={firstCatOrg} onChange={(e) => setFirstCatOrg(e.target.value)}>
                  {firstCatOrgOptions.map((o) => <MenuItem key={o.id} value={String(o.id)}>{o.name || `Org #${o.id}`}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField autoFocus fullWidth size="small" label="Category name" value={firstCatName}
              onChange={(e) => setFirstCatName(e.target.value)} sx={{ mt: firstCatOrgOptions.length !== 1 ? 0 : 1, mb: 2 }} />
            <FormControl fullWidth size="small">
              <InputLabel id="fc-type">Type</InputLabel>
              <Select labelId="fc-type" label="Type" value={firstCatType} onChange={(e) => setFirstCatType(e.target.value)}>
                {['general', 'inperson', 'drive-thru', 'online'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFirstCatOpen(false)} disabled={firstCatBusy}>Cancel</Button>
            <Button variant="contained" onClick={submitFirstCategory} disabled={firstCatBusy}>{firstCatBusy ? <CircularProgress size={18} /> : 'Create'}</Button>
          </DialogActions>
        </Dialog>

        {!loading && groupedOrgs.length > 0 && (
          <Fade in timeout={300}>
            <Box>
              {/* Org admin who isn't a category admin yet: offer to grant themselves queue access.
                  Hidden for sqip/staff admins — the self-serve grant targets their own org_access
                  (empty for platform admins), so it no-ops for them; hide it until that's built. */}
              {isOrgAdmin && !isCategoryAdmin && !isStaff && !isSqipAdmin && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2, p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>View & manage your queues</Typography>
                      <Typography variant="caption" color="text.secondary">
                        You're an organization admin. Grant yourself queue access to also act as a category
                        admin — you'll see every current and future queue for your organization.
                      </Typography>
                    </Box>
                    <Button variant="contained" onClick={grantQueueAccess} disabled={grantingQueue}>
                      {grantingQueue ? <CircularProgress size={18} color="inherit" /> : 'Grant queue access'}
                    </Button>
                  </Stack>
                </Paper>
              )}
              {isBothRoles && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
                  <Tabs
                    value={roleView === 'org' ? 0 : 1}
                    onChange={(_, v) => setRoleView(v === 0 ? 'org' : 'category')}
                    variant="fullWidth"
                  >
                    <Tab label="Organization admin" />
                    <Tab label="Queues (category admin)" />
                  </Tabs>
                </Paper>
              )}
              {/* Category tabs */}
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 3,
                }}
              >
                {/* Organization-level tabs */}
                <Tabs
                  value={orgIndex}
                  onChange={(_, v) => setOrgIndex(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 52 },
                    '& .MuiTabs-indicator': {
                      background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
                      height: 4,
                      borderRadius: 2,
                    },
                  }}
                >
                  {groupedOrgs.map((org, i) => (
                    <Tab
                      key={org.id ?? `unassigned-${i}`}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{org.name || `Org #${org.id}`}</span>
                          <Chip
                            label={`${org.categories.length}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                          />
                        </Box>
                      }
                    />
                  ))}
                </Tabs>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  {currentOrg && canManageOrg(currentOrg.id) && (
                    <>
                      <Button variant="contained" size="small" onClick={() => { setCreateForm(emptyCreateForm()); setCreateError(''); setCreateFieldErrors({}); setCreateDialogOpen(true); }} sx={{ borderRadius: 6, mr: 1 }}>
                        + New Category
                      </Button>
                      {roleView === 'org' && (
                        <Button variant="outlined" size="small" onClick={() => { setCreateUserDialogOpen(true); setCreateUserForm({ first_name: '', last_name: '', email: '', phone: '', category_ids: (currentOrg && currentOrg.categories && currentOrg.categories[0]) ? [currentOrg.categories[0].id] : [] }); }} sx={{ borderRadius: 6 }}>
                          + New Category Admin
                        </Button>
                      )}
                    </>
                  )}
                </Box>

                {/* Category tabs for the selected organization (hidden for org-admins because we show a list) */}
                {currentOrg && roleView === 'category' && (
                  <Tabs
                    value={categoryTabIndexMap[currentOrg.id] || 0}
                    onChange={(_, v) => setCategoryTabIndexMap((prev) => ({ ...prev, [currentOrg.id]: v }))}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
                      '& .MuiTabs-indicator': { background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)', height: 3, borderRadius: 2 },
                      px: 1,
                    }}
                  >
                    {currentOrg.categories.map((cat, i) => (
                      <Tab
                        key={cat.id}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{cat.name || cat.description || `Category #${cat.id}`}</span>
                            <Chip
                              label={cat.status}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: cat.status === 'active' ? '#e8f5e9' : '#fce4ec',
                                color: cat.status === 'active' ? '#2e7d32' : '#c62828',
                                border: `1px solid ${cat.status === 'active' ? '#a5d6a7' : '#ef9a9a'}`,
                              }}
                            />
                            {cat.is_scheduled && (
                              <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                )}

                {/* Create Category dialog */}
                <Dialog open={createDialogOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm" TransitionComponent={Fade} transitionDuration={{ enter: 200, exit: 100 }}>
                  <DialogTitle>Create new category</DialogTitle>
                  <DialogContent>
                    {createError && <Alert severity="error" sx={{ mb: 1 }}>{createError}</Alert>}
                    <TextField
                      fullWidth
                      required
                      label="Name"
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                      sx={{ mb: 2 }}
                      error={Boolean(createFieldErrors.name)}
                      helperText={createFieldErrors.name}
                      inputRef={createNameRef}
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={createForm.description}
                      onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                      sx={{ mb: 2 }}
                      error={Boolean(createFieldErrors.description)}
                      helperText={createFieldErrors.description}
                      inputRef={createDescriptionRef}
                    />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1 }}>Wait time</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="How many people can you serve at once?"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={createForm.service_points}
                      onChange={(e) => setCreateForm((f) => ({ ...f, service_points: e.target.value }))}
                      helperText="Booths, chairs, bays or staff working in parallel. Clinic with one doctor = 1; restaurant with 5 booths = 5."
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Typical time per person (minutes)"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={createForm.estimated_time}
                      onChange={(e) => setCreateForm((f) => ({ ...f, estimated_time: e.target.value }))}
                      helperText={createFieldErrors.estimated_time || 'Starting estimate only — once the queue has real traffic, sqip measures the actual pace automatically.'}
                      error={Boolean(createFieldErrors.estimated_time)}
                      sx={{ mb: 2 }}
                      inputRef={createEstimatedRef}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Show &quot;1 hr+&quot; above (minutes)"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={createForm.wait_display_cap_minutes}
                      onChange={(e) => setCreateForm((f) => ({ ...f, wait_display_cap_minutes: e.target.value }))}
                      helperText="Longer waits show as e.g. “1 hr+” instead of an exact number. Leave blank to always show the estimate."
                      sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                      control={<Switch checked={!!createForm.show_wait_time} onChange={(e) => setCreateForm((f) => ({ ...f, show_wait_time: e.target.checked }))} />}
                      label={<Typography variant="body2">Show estimated wait to customers</Typography>}
                      sx={{ mb: 2, display: 'block' }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address line 1 (optional)"
                      value={createForm.address_line1}
                      onChange={(e) => setCreateForm((f) => ({ ...f, address_line1: e.target.value }))}
                      helperText={createFieldErrors.address_line1 || 'Street address specific to this service location'}
                      error={Boolean(createFieldErrors.address_line1)}
                      sx={{ mb: 2 }}
                      inputRef={createAddress1Ref}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address line 2 (optional)"
                      value={createForm.address_line2}
                      onChange={(e) => setCreateForm((f) => ({ ...f, address_line2: e.target.value }))}
                      helperText={createFieldErrors.address_line2 || 'Floor, suite, landmark'}
                      error={Boolean(createFieldErrors.address_line2)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Pincode / ZIP (optional)"
                      value={createForm.pincode}
                      onChange={(e) => setCreateForm((f) => ({ ...f, pincode: e.target.value }))}
                      helperText={createFieldErrors.pincode || 'Postal / ZIP code'}
                      error={Boolean(createFieldErrors.pincode)}
                      sx={{ mb: 2 }}
                      inputRef={createPincodeRef}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone number (optional)"
                      value={createForm.phone_number}
                      onChange={(e) => setCreateForm((f) => ({ ...f, phone_number: e.target.value }))}
                      helperText={createFieldErrors.phone_number || 'E.164 format, e.g. +911234567890'}
                      error={Boolean(createFieldErrors.phone_number)}
                      sx={{ mb: 2 }}
                      inputRef={createPhoneRef}
                    />
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="cat-type-label">Type</InputLabel>
                      <Select labelId="cat-type-label" value={createForm.type} label="Type" onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}>
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="inperson">In Person</MenuItem>
                        <MenuItem value="drive-thru">Drive-thru</MenuItem>
                        <MenuItem value="online">Online</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel control={<Switch checked={createForm.is_scheduled} onChange={(e) => setCreateForm((f) => ({ ...f, is_scheduled: e.target.checked }))} />} label="Scheduled appointments" />
                    {createForm.is_scheduled && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="create-interval-label">Slot interval</InputLabel>
                            <Select
                              labelId="create-interval-label"
                              value={createForm.time_interval_per_appointment || 15}
                              label="Slot interval"
                              onChange={(e) => setCreateForm((f) => ({ ...f, time_interval_per_appointment: Number(e.target.value) }))}
                            >
                              {intervalOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{formatIntervalLabel(opt)}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Opening hours</Typography>
                        <Grid container spacing={1}>
                          {DAYS_OF_WEEK.map((day) => {
                            const opening = (createForm.opening_hours && createForm.opening_hours[day]) || [];
                            const isClosed = !opening || opening.length === 0;
                            const br = (createForm.break_hours && createForm.break_hours[day]) || [];
                            return (
                              <Grid item xs={12} sm={6} key={`open-${day}`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 110 }}><Typography variant="body2">{day}</Typography></Box>
                                  <FormControlLabel
                                    control={<Switch size="small" checked={!isClosed} onChange={(e) => toggleDayOpenCreate(day, e.target.checked)} />}
                                    label={isClosed ? 'Closed' : 'Open'}
                                  />
                                </Box>
                                {!isClosed && (
                                  <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'flex-start' }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Opening</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                          <TimeInput
                                            value24={opening[0] ? opening[0][0] : '09:00'}
                                            onChange={(val) => {
                                              setCreateForm((prev) => {
                                                const oh = { ...(prev.opening_hours || {}) };
                                                oh[day] = [[val, (oh[day] && oh[day][0] && oh[day][0][1]) || '17:00']];
                                                return { ...prev, opening_hours: oh };
                                              });
                                            }}
                                          />
                                          <TimeInput
                                            value24={opening[0] ? opening[0][1] : '17:00'}
                                            onChange={(val) => {
                                              setCreateForm((prev) => {
                                                const oh = { ...(prev.opening_hours || {}) };
                                                oh[day] = [[(oh[day] && oh[day][0] && oh[day][0][0]) || '09:00', val]];
                                                return { ...prev, opening_hours: oh };
                                              });
                                            }}
                                          />
                                        </Box>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                                        <Button size="small" variant="text" fullWidth={window.innerWidth < 600} onClick={() => {
                                          setCreateForm((prev) => {
                                            const bh = { ...(prev.break_hours || {}) };
                                            if (bh[day] && bh[day].length > 0) bh[day] = [];
                                            else bh[day] = [['13:00','14:00']];
                                            return { ...prev, break_hours: bh };
                                          });
                                        }} sx={{ color: 'primary.main', textTransform: 'none', fontSize: 13, whiteSpace: 'nowrap' }}>{br && br.length > 0 ? 'Remove break' : 'Add break'}</Button>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                {br && br.length > 0 && (
                                  <Box sx={{ mt: 1, pl: { xs: 0, sm: 1 } }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'flex-start' }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'warning.dark' }}>Break</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#fff7e6', p: 0.5, borderRadius: 1 }}>
                                          <TimeInput
                                            value24={br[0][0]}
                                            onChange={(val) => {
                                              setCreateForm((prev) => {
                                                const bh = { ...(prev.break_hours || {}) };
                                                bh[day] = [[val, (bh[day] && bh[day][0] && bh[day][0][1]) || '14:00']];
                                                return { ...prev, break_hours: bh };
                                              });
                                            }}
                                          />
                                          <TimeInput
                                            value24={br[0][1]}
                                            onChange={(val) => {
                                              setCreateForm((prev) => {
                                                const bh = { ...(prev.break_hours || {}) };
                                                bh[day] = [[(bh[day] && bh[day][0] && bh[day][0][0]) || '13:00', val]];
                                                return { ...prev, break_hours: bh };
                                              });
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                {createDayErrors && createDayErrors[day] && (
                                  <FormHelperText error sx={{ mt: 0.5 }}>{createDayErrors[day]}</FormHelperText>
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={closeCreateDialog}>Cancel</Button>
                    <Button onClick={handleCreateCategory} variant="contained" disabled={createLoading}>{createLoading ? <CircularProgress size={18} /> : 'Create'}</Button>
                  </DialogActions>
                </Dialog>

                {/* Create Category Admin (org-scoped) dialog */}
                <Dialog open={createUserDialogOpen} onClose={closeCreateUserDialog} fullWidth maxWidth="sm">
                  <DialogTitle>Create category admin</DialogTitle>
                  <DialogContent>
                    {createUserError && <Alert severity="error" sx={{ mb: 1 }}>{createUserError}</Alert>}
                    <Box component="form" sx={{ mt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                              <TextField
                                required
                                size="small"
                                label="First name"
                                value={createUserForm.first_name}
                                onChange={(e) => setCreateUserForm((f) => ({ ...f, first_name: e.target.value }))}
                                fullWidth
                                error={Boolean(createUserFieldErrors.first_name)}
                                helperText={createUserFieldErrors.first_name}
                                inputRef={createUserFirstNameRef}
                              />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Last name"
                            value={createUserForm.last_name}
                            onChange={(e) => setCreateUserForm((f) => ({ ...f, last_name: e.target.value }))}
                            fullWidth
                            error={Boolean(createUserFieldErrors.last_name)}
                            helperText={createUserFieldErrors.last_name}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Email (optional)"
                            value={createUserForm.email}
                            onChange={(e) => setCreateUserForm((f) => ({ ...f, email: e.target.value }))}
                            fullWidth
                            error={Boolean(createUserFieldErrors.email)}
                            helperText={createUserFieldErrors.email}
                            inputRef={createUserEmailRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Phone (optional)"
                            value={createUserForm.phone}
                            onChange={(e) => setCreateUserForm((f) => ({ ...f, phone: e.target.value }))}
                            fullWidth
                            error={Boolean(createUserFieldErrors.phone)}
                            helperText={createUserFieldErrors.phone || 'Include country code or use +prefix (optional)'}
                            inputRef={createUserPhoneRef}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => setCountryAnchorEl(e.currentTarget)}
                                    sx={{ mr: 0, p: 0, minWidth: 36, display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <Typography sx={{ fontSize: 14 }}>{selectedCountry && selectedCountry.flag}</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 0.5 }}>{selectedCountry && selectedCountry.dial}</Typography>
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          {((currentOrg && currentOrg.categories) || []).length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              Create a category (queue) first — a category admin needs at least one
                              category to moderate. Use “+ New Category” above, then come back to add
                              the admin.
                            </Alert>
                          ) : (
                          <FormControl fullWidth size="small" error={Boolean(createUserFieldErrors.category_ids)}>
                            <InputLabel id="create-user-cat-label">Category</InputLabel>
                            <Select
                              labelId="create-user-cat-label"
                              multiple
                              value={createUserForm.category_ids || []}
                              label="Category"
                              onChange={(e) => setCreateUserForm((f) => ({ ...f, category_ids: e.target.value }))}
                              inputRef={createUserCategoryRef}
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) return 'Select categories';
                                const selIds = (selected || []).map((s) => (typeof s === 'string' && s.match(/^\d+$/) ? parseInt(s, 10) : s));
                                const names = (currentOrg && currentOrg.categories || []).filter((c) => selIds.includes(c.id)).map((c) => c.name || c.description || `#${c.id}`);
                                const visible = names.slice(0, 2);
                                const remaining = names.length - visible.length;
                                return (
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {visible.map((n) => <Chip key={n} label={n} size="small" sx={{ mr: 0.5 }} />)}
                                    {remaining > 0 && <Chip label={`+${remaining} more`} size="small" sx={{ bgcolor: 'transparent', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }} />}
                                  </Box>
                                );
                              }}
                              MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
                            >
                              {(currentOrg && currentOrg.categories || []).map((c) => (
                                <MenuItem key={`cu-${c.id}`} value={c.id}>
                                  <Checkbox checked={Array.isArray(createUserForm.category_ids) ? createUserForm.category_ids.map((s) => (typeof s === 'string' && s.match(/^\d+$/) ? parseInt(s, 10) : s)).includes(c.id) : false} />
                                  <ListItemText primary={c.name || c.description || `Category #${c.id}`} />
                                </MenuItem>
                              ))}
                            </Select>
                            {createUserFieldErrors.category_ids && (
                              <FormHelperText error sx={{ mt: 0.5 }}>{createUserFieldErrors.category_ids}</FormHelperText>
                            )}
                          </FormControl>
                          )}
                        </Grid>

                        {/* Optional login credentials — only when creating a new admin */}
                        {!editingUserId && (
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                              Set login credentials (optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                              Username/password login is optional — category admin can always use phone number + OTP instead.
                            </Typography>
                            <UsernamePasswordFields
                              username={credUsername} setUsername={setCredUsername}
                              password={credPassword} setPassword={setCredPassword}
                              confirm={credConfirm} setConfirm={setCredConfirm}
                              onValidity={setCredValid}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: 'space-between' }}>
                    {editingUserId ? (
                      <Button color="error" onClick={() => handleDeleteUserAccount(editingUserId)} disabled={createUserLoading}>
                        Delete user
                      </Button>
                    ) : <span />}
                    <Box>
                      <Button onClick={closeCreateUserDialog} disabled={createUserLoading}>Cancel</Button>
                      <Button onClick={handleCreateCategoryUser} variant="contained" disabled={createUserLoading}>{createUserLoading ? <CircularProgress size={18} /> : (editingUserId ? 'Update' : 'Create')}</Button>
                    </Box>
                  </DialogActions>
                </Dialog>

                {/* Delete confirmation dialog */}
                <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                  <DialogTitle>Delete category?</DialogTitle>
                  <DialogContent>
                    <Typography>Delete category "{deleteTarget && (deleteTarget.name || deleteTarget.description || `#${deleteTarget.id}`)}"?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      If anyone is currently waiting in this queue, the delete is blocked until it's cleared.
                      If the queue has past history, it will be archived (hidden) instead of permanently deleted.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteCategory}>Delete</Button>
                  </DialogActions>
                </Dialog>

                {/* Edit Category dialog */}
                <Dialog open={editCatDialogOpen} onClose={() => { 
                  setEditCatDialogOpen(false); 
                  setEditFieldErrors({}); 
                  setEditCatError(''); 
                }} fullWidth maxWidth="sm" TransitionComponent={Fade} transitionDuration={{ enter: 200, exit: 100 }}>
                  <DialogTitle>Edit category</DialogTitle>
                  <DialogContent>
                    {editCatError && <Alert severity="error" sx={{ mb: 1 }}>{editCatError}</Alert>}
                    <Box component="form" sx={{ mt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            required
                            size="small"
                            label="Name"
                            value={editCatForm.name}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, name: e.target.value }))}
                            fullWidth
                            error={Boolean(editFieldErrors.name)}
                            helperText={editFieldErrors.name}
                            inputRef={editNameRef}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Description"
                            value={editCatForm.description}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, description: e.target.value }))}
                            fullWidth
                            error={Boolean(editFieldErrors.description)}
                            helperText={editFieldErrors.description}
                            inputRef={editDescriptionRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Served at once (booths/staff)"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={editCatForm.service_points}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, service_points: e.target.value }))}
                            helperText={editFieldErrors.service_points || 'How many customers you can serve in parallel (1 = one at a time)'}
                            error={Boolean(editFieldErrors.service_points)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Typical time per person (min)"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={editCatForm.estimated_time}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, estimated_time: e.target.value }))}
                            helperText={editFieldErrors.estimated_time || 'Starting estimate — replaced by the measured pace once there is traffic'}
                            error={Boolean(editFieldErrors.estimated_time)}
                            fullWidth
                            inputRef={editEstimatedRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label={'Show "1 hr+" above (min)'}
                            type="number"
                            inputProps={{ min: 1 }}
                            value={editCatForm.wait_display_cap_minutes}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, wait_display_cap_minutes: e.target.value }))}
                            helperText={editFieldErrors.wait_display_cap_minutes || 'Blank = always show the exact estimate'}
                            error={Boolean(editFieldErrors.wait_display_cap_minutes)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={<Switch checked={!!editCatForm.show_wait_time} onChange={(e) => setEditCatForm((f) => ({ ...f, show_wait_time: e.target.checked }))} />}
                            label={<Typography variant="body2">Show wait time to customers</Typography>}
                            sx={{ m: 0 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Phone (optional)"
                            value={editCatForm.phone_number}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, phone_number: e.target.value }))}
                            helperText={editFieldErrors.phone_number || 'E.164, e.g. +911234567890'}
                            error={Boolean(editFieldErrors.phone_number)}
                            fullWidth
                            inputRef={editPhoneRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Address line 1 (optional)"
                            value={editCatForm.address_line1}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, address_line1: e.target.value }))}
                            fullWidth
                            helperText={editFieldErrors.address_line1}
                            error={Boolean(editFieldErrors.address_line1)}
                            inputRef={editAddress1Ref}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Address line 2 (optional)"
                            value={editCatForm.address_line2}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, address_line2: e.target.value }))}
                            fullWidth
                            helperText={editFieldErrors.address_line2}
                            error={Boolean(editFieldErrors.address_line2)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Pincode / ZIP (optional)"
                            value={editCatForm.pincode}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, pincode: e.target.value }))}
                            fullWidth
                            helperText={editFieldErrors.pincode}
                            error={Boolean(editFieldErrors.pincode)}
                            inputRef={editPincodeRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Max advance days"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={editCatForm.max_advance_days}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, max_advance_days: e.target.value }))}
                            helperText={editFieldErrors.max_advance_days || 'How many days ahead users can book'}
                            error={Boolean(editFieldErrors.max_advance_days)}
                            fullWidth
                            inputRef={editMaxAdvanceRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Max bookings per user per day"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={editCatForm.max_scheduled_per_user_per_day}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, max_scheduled_per_user_per_day: e.target.value }))}
                            helperText={editFieldErrors.max_scheduled_per_user_per_day || 'Leave blank for unlimited'}
                            error={Boolean(editFieldErrors.max_scheduled_per_user_per_day)}
                            fullWidth
                            inputRef={editMaxPerDayRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            size="small"
                            label="Timezone"
                            value={editCatForm.time_zone}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, time_zone: e.target.value }))}
                            helperText={editFieldErrors.time_zone || 'e.g. Asia/Kolkata, America/New_York'}
                            error={Boolean(editFieldErrors.time_zone)}
                            fullWidth
                            inputRef={editTimezoneRef}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="edit-cat-type-label">Type</InputLabel>
                            <Select
                              labelId="edit-cat-type-label"
                              value={editCatForm.type}
                              label="Type"
                              onChange={(e) => setEditCatForm((f) => ({ ...f, type: e.target.value }))}
                            >
                              <MenuItem value="general">General</MenuItem>
                              <MenuItem value="inperson">In Person</MenuItem>
                              <MenuItem value="drive-thru">Drive-thru</MenuItem>
                              <MenuItem value="online">Online</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={<Switch checked={editCatForm.is_scheduled} onChange={(e) => setEditCatForm((f) => ({ ...f, is_scheduled: e.target.checked }))} />}
                            label="Scheduled appointments"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Queue-position SMS alerts"
                            placeholder="e.g. 5, 3, 1"
                            value={editCatForm.notify_queue_positions || ''}
                            onChange={(e) => setEditCatForm((f) => ({ ...f, notify_queue_positions: e.target.value }))}
                            helperText="Positions that text a waiting customer. Empty = off."
                          />
                        </Grid>
                        {editCatForm.is_scheduled && (
                          <Grid item xs={12}>
                            <Box sx={{ mt: 2, mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                  <InputLabel id="edit-interval-label">Slot interval</InputLabel>
                                  <Select
                                    labelId="edit-interval-label"
                                    value={editCatForm.time_interval_per_appointment || 15}
                                    label="Slot interval"
                                    onChange={(e) => setEditCatForm((f) => ({ ...f, time_interval_per_appointment: Number(e.target.value) }))}
                                  >
                                      {intervalOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{formatIntervalLabel(opt)}</MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Opening hours</Typography>
                              <Grid container spacing={1}>
                                {DAYS_OF_WEEK.map((day) => {
                                  const opening = (editCatForm.opening_hours && editCatForm.opening_hours[day]) || [];
                                  const isClosed = !opening || opening.length === 0;
                                  const br = (editCatForm.break_hours && editCatForm.break_hours[day]) || [];
                                  return (
                                    <Grid item xs={12} sm={6} key={`edit-open-${day}`}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 110 }}><Typography variant="body2">{day}</Typography></Box>
                                        <FormControlLabel
                                          control={<Switch size="small" checked={!isClosed} onChange={(e) => toggleDayOpenEdit(day, e.target.checked)} />}
                                          label={isClosed ? 'Closed' : 'Open'}
                                        />
                                      </Box>
                                      {!isClosed && (
                                        <Box sx={{ mt: 1 }}>
                                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Opening</Typography>
                                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TimeInput
                                                  value24={opening[0] ? opening[0][0] : '09:00'}
                                                  onChange={(val) => {
                                                    setEditCatForm((prev) => {
                                                      const oh = { ...(prev.opening_hours || {}) };
                                                      oh[day] = [[val, (oh[day] && oh[day][0] && oh[day][0][1]) || '17:00']];
                                                      return { ...prev, opening_hours: oh };
                                                    });
                                                  }}
                                                />
                                                <TimeInput
                                                  value24={opening[0] ? opening[0][1] : '17:00'}
                                                  onChange={(val) => {
                                                    setEditCatForm((prev) => {
                                                      const oh = { ...(prev.opening_hours || {}) };
                                                      oh[day] = [[(oh[day] && oh[day][0] && oh[day][0][0]) || '09:00', val]];
                                                      return { ...prev, opening_hours: oh };
                                                    });
                                                  }}
                                                />
                                              </Box>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                                              <Button size="small" variant="text" fullWidth={window.innerWidth < 600} onClick={() => {
                                                setEditCatForm((prev) => {
                                                  const bh = { ...(prev.break_hours || {}) };
                                                  if (bh[day] && bh[day].length > 0) bh[day] = [];
                                                  else bh[day] = [['13:00','14:00']];
                                                  return { ...prev, break_hours: bh };
                                                });
                                              }} sx={{ color: 'primary.main', textTransform: 'none', fontSize: 13, whiteSpace: 'nowrap' }}>{br && br.length > 0 ? 'Remove break' : 'Add break'}</Button>
                                            </Box>
                                          </Box>
                                        </Box>
                                      )}
                                      {br && br.length > 0 && (
                                        <Box sx={{ mt: 1, pl: { xs: 0, sm: 1 } }}>
                                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                                              <Typography variant="caption" sx={{ color: 'warning.dark' }}>Break</Typography>
                                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: '#fff7e6', p: 0.5, borderRadius: 1 }}>
                                                <TimeInput
                                                  value24={br[0][0]}
                                                  onChange={(val) => {
                                                    setEditCatForm((prev) => {
                                                      const bh = { ...(prev.break_hours || {}) };
                                                      bh[day] = [[val, (bh[day] && bh[day][0] && bh[day][0][1]) || '14:00']];
                                                      return { ...prev, break_hours: bh };
                                                    });
                                                  }}
                                                />
                                                <TimeInput
                                                  value24={br[0][1]}
                                                  onChange={(val) => {
                                                    setEditCatForm((prev) => {
                                                      const bh = { ...(prev.break_hours || {}) };
                                                      bh[day] = [[(bh[day] && bh[day][0] && bh[day][0][0]) || '13:00', val]];
                                                      return { ...prev, break_hours: bh };
                                                    });
                                                  }}
                                                />
                                              </Box>
                                            </Box>
                                          </Box>
                                        </Box>
                                      )}
                                      {editDayErrors && editDayErrors[day] && (
                                        <FormHelperText error sx={{ mt: 0.5 }}>{editDayErrors[day]}</FormHelperText>
                                      )}
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setEditCatDialogOpen(false)} disabled={editCatLoading}>Cancel</Button>
                    <Button onClick={handleEditCategory} variant="contained" disabled={editCatLoading}>
                      {editCatLoading ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Org-admin simplified view: categories + create category-admin users (no appointments) */}
                {currentOrg && roleView === 'org' && (
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Org status is read-only for org admins — only sqip can activate/deactivate an org. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700} noWrap>{currentOrg.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Organization status is managed by sqip.</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {currentOrg.slug && (
                          <ShareButton
                            url={`${window.location.origin}/org/${currentOrg.slug}`}
                            title={currentOrg.name}
                            text={`Join the queue at ${currentOrg.name} on sqip`}
                            tooltip="Share organization link"
                          />
                        )}
                        <Button size="small" variant="outlined" onClick={() => setInviteFor({ orgId: currentOrg.id, category: null })} sx={{ borderRadius: 6 }}>
                          Send org link
                        </Button>
                        <Chip
                          size="small"
                          label={currentOrg.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
                          color={currentOrg.status === 'active' ? 'success' : 'default'}
                          variant={currentOrg.status === 'active' ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Categories</Typography>
                    {currentOrg.categories.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No categories yet. Use “+ New Category” above to create your first queue.
                      </Typography>
                    )}
                    <Stack spacing={1}>
                      {currentOrg.categories.map((cat) => (
                        <Paper key={`org-cat-${cat.id}`} elevation={0} sx={{ p: 1.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-start', border: '2px solid', borderColor: movedCatId === cat.id ? 'primary.main' : 'divider', borderRadius: 2, position: 'relative', minHeight: { xs: 'auto', sm: 140 }, '@keyframes catMovedFlash': { '0%': { backgroundColor: 'rgba(25,118,210,0.40)', transform: 'scale(1.02)', boxShadow: '0 10px 28px rgba(25,118,210,0.45)' }, '55%': { backgroundColor: 'rgba(25,118,210,0.20)' }, '100%': { backgroundColor: 'transparent', transform: 'scale(1)', boxShadow: 'none' } }, animation: movedCatId === cat.id ? 'catMovedFlash 1.6s ease-out' : 'none' }}>
                          {movedCatId === cat.id && (
                            <Chip size="small" color="primary" label="Moved" sx={{ position: 'absolute', top: 8, right: 8, fontWeight: 700, zIndex: 2 }} />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={700}>{cat.name || cat.description || `Category #${cat.id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">Type: {cat.type || 'general'} {cat.is_scheduled ? ' • Scheduled' : ''}</Typography>
                            {/* Assigned admins for this category */}
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              {categoryAdmins && categoryAdmins.filter((a) => Array.isArray(a.category_ids) && a.category_ids.includes(cat.id)).map((a) => (
                                <Chip
                                  key={`admin-${a.id}-${cat.id}`}
                                  label={`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username}
                                  size="small"
                                  clickable
                                  onClick={() => handleOpenEditUser(a)}
                                  avatar={<Avatar>{(a.first_name && a.first_name[0]) || (a.username && a.username[0]) || 'U'}</Avatar>}
                                  sx={{ mr: 0.5 }}
                                  deleteIcon={<DeleteIcon />}
                                  onDelete={() => handleDeleteCategoryUser(a.id)}
                                />
                              ))}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', alignSelf: { xs: 'stretch', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' } }}>
                            {currentOrg.categories.length > 1 && (
                              <FormControl size="small" sx={{ minWidth: 78 }}>
                                <InputLabel id={`ord-${cat.id}`}>Order</InputLabel>
                                <Select
                                  labelId={`ord-${cat.id}`}
                                  label="Order"
                                  value={currentOrg.categories.findIndex((c) => c.id === cat.id) + 1}
                                  onChange={(e) => handleReorderCategory(cat, Number(e.target.value))}
                                >
                                  {currentOrg.categories.map((_, i) => (
                                    <MenuItem key={i} value={i + 1}>{i + 1}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            <Button size="small" onClick={() => { setEditingUserId(null); setCreateUserDialogOpen(true); setCreateUserForm({ first_name: '', last_name: '', email: '', phone: '', category_ids: [cat.id] }); }} fullWidth={window.innerWidth < 600}>Create Admin</Button>
                            <Button size="small" variant="outlined" onClick={() => handleOpenEditCat(cat)} fullWidth={window.innerWidth < 600}>Edit</Button>
                            <Button size="small" onClick={() => setInviteFor({ orgId: currentOrg.id, category: cat })} fullWidth={window.innerWidth < 600}>Send link</Button>
                            {currentOrg.slug && cat.slug && (
                              <ShareButton
                                url={`${window.location.origin}/org/${currentOrg.slug}/category/${cat.slug}`}
                                title={cat.name || cat.description || 'sqip'}
                                text={`Join the "${cat.name || cat.description}" queue on sqip`}
                                tooltip="Share category link"
                              />
                            )}
                            <FormControlLabel
                              control={<Switch checked={cat.status === 'active'} onChange={() => handleToggleStatus(cat)} />}
                              label={<Typography variant="body2" fontWeight={600}>{cat.status === 'active' ? '🟢 Active' : '🔴 Inactive'}</Typography>}
                              sx={{ m: 0, minWidth: { xs: '100%', sm: 'auto' } }}
                            />
                            {canManageOrg(cat.organization) && (
                              <Button size="small" color="error" variant="outlined" onClick={() => { setDeleteTarget(cat); setDeleteConfirmOpen(true); }} fullWidth={window.innerWidth < 600}>Delete</Button>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                    {currentOrg.id && (
                      <FacadeManager orgId={currentOrg.id} orgSlug={currentOrg.slug} categories={currentOrg.categories} />
                    )}
                    {/* Business profile + away mode, then photo & login credentials */}
                    {currentOrg.id && canManageOrg(currentOrg.id) && (
                      <>
                        <OrgProfileCard orgId={currentOrg.id} />
                        <OrgSettingsCards orgId={currentOrg.id} />
                      </>
                    )}
                  </Box>
                )}

                {/* Category panel content */}
                {currentCategory && roleView === 'category' && (
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Category info + status toggle */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {currentCategory.name || currentCategory.description || `Category #${currentCategory.id}`}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              Type: {currentCategory.type || 'general'}
                            </Typography>
                          </Box>
                          {currentCategory.is_scheduled && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Scheduled appointments
                              </Typography>
                            </Box>
                          )}
                          {currentCategory.time_zone && (
                            <Typography variant="caption" color="text.secondary">
                              TZ: {currentCategory.time_zone}
                            </Typography>
                          )}
                        </Stack>
                        {currentCategory.description && currentCategory.name && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {currentCategory.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Actions. Grouped into rows rather than one free-wrapping line: on a
                           phone seven controls wrapped arbitrarily, and the two share icons ended
                           up side by side with nothing to tell them apart. */}
                      <Stack spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' }, alignItems: { sm: 'flex-end' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 1 }}>
                        {statusUpdating[currentCategory.id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={currentCategory.status === 'active'}
                                onChange={() => handleToggleStatus(currentCategory)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2" fontWeight={600}>
                                {currentCategory.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
                              </Typography>
                            }
                          />
                        )}

                      </Box>

                      {/* Invite/share. Each share icon sits next to the button it belongs to, so
                          "this queue" and "whole org" can be told apart at a glance. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setInviteFor({ orgId: currentCategory.organization, category: currentCategory })}
                        >
                          Send link
                        </Button>
                        {currentOrg?.slug && currentCategory.slug && (
                          <ShareButton
                            url={`${window.location.origin}/org/${currentOrg.slug}/category/${currentCategory.slug}`}
                            title={currentCategory.name || currentCategory.description || 'sqip'}
                            text={`Join the "${currentCategory.name || currentCategory.description}" queue on sqip`}
                            tooltip="Share this queue link"
                          />
                        )}
                        <Box sx={{ width: 8 }} />
                        <Button
                          size="small"
                          onClick={() => setInviteFor({ orgId: currentCategory.organization, category: null })}
                        >
                          Send org link
                        </Button>
                        {currentOrg?.slug && (
                          <ShareButton
                            url={`${window.location.origin}/org/${currentOrg.slug}`}
                            title={currentOrg.name}
                            text={`Join the queue at ${currentOrg.name} on sqip`}
                            tooltip="Share organization link"
                          />
                        )}
                      </Box>

                      {/* Primary action + destructive, on their own row. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 1 }}>

                        {/* Add to queue button for UNSCHEDULED categories */}
                        {!currentCategory.is_scheduled && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DirectionsWalkIcon />}
                            onClick={() => {
                              setQueueModalCategory(currentCategory);
                              setQueueForm({ first_name: '', last_name: '', phone: '', email: '', notify: 'both' });
                              setQueueError('');
                              setQueueFormErrors({});
                              // Detect country for Add-to-queue: prefer timezone -> navigator.language -> fallback to India
                              try {
                                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                const tzMap = {
                                  'Asia/Kolkata': 'IN',
                                  'Europe/London': 'GB',
                                  'America/New_York': 'US',
                                  'Australia/Sydney': 'AU',
                                  'America/Toronto': 'CA',
                                };
                                let code = tz && tzMap[tz];
                                if (!code && typeof navigator !== 'undefined' && navigator.language) {
                                  const parts = navigator.language.split('-');
                                  if (parts.length > 1) code = parts[1].toUpperCase();
                                }
                                const sel = COUNTRIES.find((c) => c.code === code) || detectDefaultCountry();
                                setSelectedCountry(sel);
                              } catch (e) {
                                setSelectedCountry(detectDefaultCountry());
                              }
                              setQueueModalOpen(true);
                            }}
                          >
                            {`Add to queue${unscheduledCounts[currentCategory.id] ? ` (${unscheduledCounts[currentCategory.id]} waiting)` : ''}`}
                          </Button>
                        )}

                        {/* Delete category (org-admin or staff) */}
                        {canManageOrg(currentCategory.organization) && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => { setDeleteTarget(currentCategory); setDeleteConfirmOpen(true); }}
                          >
                            Delete
                          </Button>
                        )}
                      </Box>
                      </Stack>
                    </Box>

                    {/* Combined Hours card: Opening Hours + Break Hours for scheduled categories */}
                    {currentCategory && currentCategory.is_scheduled && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: 3,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: '#f7f4ff',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#6a1b9a', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} /> Hours
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: (theme) => theme.palette.custom ? theme.palette.custom.teal : '#00C4CC', display: 'block', mb: 1 }}>
                              Opening Hours
                            </Typography>
                            {Object.keys(currentCategory.opening_hours || {}).length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {Object.entries(currentCategory.opening_hours).map(([day, ranges]) => (
                                  <Chip
                                    key={`open-${day}`}
                                    size="small"
                                    label={
                                      ranges && Array.isArray(ranges) && ranges.length > 0
                                        ? `${day.slice(0, 3)}: ${ranges[0][0]}–${ranges[0][1]}`
                                        : `${day.slice(0, 3)}: Closed`
                                    }
                                    sx={{
                                      fontSize: 11,
                                      bgcolor: ranges && Array.isArray(ranges) && ranges.length > 0 ? '#ede7f6' : '#f5f5f5',
                                      color: ranges && Array.isArray(ranges) && ranges.length > 0 ? '#4a148c' : '#9e9e9e',
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">No opening hours configured.</Typography>
                            )}
                            {currentCategory.time_interval_per_appointment && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Slot interval: {typeof currentCategory.time_interval_per_appointment === 'string'
                                  ? currentCategory.time_interval_per_appointment
                                  : `${currentCategory.time_interval_per_appointment} min`}
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: '#8a4b00', display: 'block', mb: 1 }}>
                              Break Hours
                            </Typography>
                            {Object.keys(currentCategory.break_hours || {}).length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {Object.entries(currentCategory.break_hours).map(([day, ranges]) => (
                                  <Chip
                                    key={`break-${day}`}
                                    size="small"
                                    label={
                                      ranges && Array.isArray(ranges) && ranges.length > 0
                                        ? `${day.slice(0, 3)}: ${ranges.map((r) => `${r[0]}–${r[1]}`).join(', ')}`
                                        : `${day.slice(0, 3)}: —`
                                    }
                                    sx={{
                                      fontSize: 11,
                                      bgcolor: ranges && Array.isArray(ranges) && ranges.length > 0 ? '#fff3e0' : '#f5f5f5',
                                      color: ranges && Array.isArray(ranges) && ranges.length > 0 ? '#e65100' : '#9e9e9e',
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">No break hours configured for this category.</Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {/* Appointments list for this category */}
                    <CategoryPanel category={currentCategory} refreshKey={queueRefreshKey} />
                  </Box>
                )}
              </Paper>
            </Box>
          </Fade>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Shared country selector menu used by phone inputs (queue + create-user) */}
      <Menu
        anchorEl={countryAnchorEl}
        open={Boolean(countryAnchorEl)}
        onClose={() => setCountryAnchorEl(null)}
      >
        {COUNTRIES.map((c) => (
          <MenuItem
            key={c.code}
            onClick={() => {
              setSelectedCountry(c);
              setCountryAnchorEl(null);
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>{c.flag}<Typography sx={{ ml: 0.5 }}>{c.label} ({c.dial})</Typography></Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Add User to Queue Modal */}
      <Dialog open={queueModalOpen} onClose={closeQueueModal} fullWidth maxWidth="sm">
        <DialogTitle>Add user to queue — {queueModalCategory ? (queueModalCategory.name || `Category #${queueModalCategory.id}`) : ''}</DialogTitle>
        <DialogContent>
          {queueError && <Alert severity="error" sx={{ mb: 2 }}>{queueError}</Alert>}
          {queuePreview && queuePreview.count > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{`There are ${queuePreview.count} people currently waiting.`}</Typography>
              <Typography variant="caption" color="text.secondary">You'll be added after them.</Typography>
            </Box>
          )}
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="First name"
                  value={queueForm.first_name}
                  onChange={(e) => setQueueForm((f) => ({ ...f, first_name: e.target.value }))}
                  fullWidth
                  error={!!queueFormErrors.first_name}
                  helperText={queueFormErrors.first_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Last name"
                  value={queueForm.last_name}
                  onChange={(e) => setQueueForm((f) => ({ ...f, last_name: e.target.value }))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  size="small"
                  label="Phone"
                  value={queueForm.phone}
                  onChange={(e) => setQueueForm((f) => ({ ...f, phone: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => setCountryAnchorEl(e.currentTarget)}
                          sx={{ mr: 0, p: 0, minWidth: 36, display: 'inline-flex', alignItems: 'center' }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: 1 }}>{selectedCountry.flag}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 0.5 }}>{selectedCountry.dial}</Typography>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!queueFormErrors.phone}
                  helperText={queueFormErrors.phone || (() => {
                    const examples = { US: '202 555 0100', CA: '416 555 0100', IN: '98765 43210', GB: '7911 123456', AU: '412 345 678' };
                    const ex = examples[selectedCountry?.code] || '10-digit local number';
                    return `Enter local number without country code, e.g. ${ex}`;
                  })()}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Email (optional)"
                  value={queueForm.email}
                  onChange={(e) => setQueueForm((f) => ({ ...f, email: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Notify the customer
                </Typography>
                <ToggleButtonGroup
                  exclusive size="small" fullWidth sx={{ mt: 0.5 }}
                  value={queueForm.notify || 'both'}
                  onChange={(_, v) => v && setQueueForm((f) => ({ ...f, notify: v }))}
                >
                  <ToggleButton value="sms">Text</ToggleButton>
                  <ToggleButton value="email">Email</ToggleButton>
                  <ToggleButton value="both">Both</ToggleButton>
                  <ToggleButton value="none">Don’t notify</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQueueModal} disabled={queueLoading}>Cancel</Button>
          <Button
            onClick={submitQueueForm}
            variant="contained"
            disabled={queueLoading}
            sx={{
              borderRadius: 3,
              background: (theme) => theme.palette.custom ? theme.palette.custom.gradientPrimary : 'var(--gradient-primary)',
              color: '#fff',
              px: 3,
              '&:hover': { opacity: 0.95 },
            }}
          >
            {queueLoading ? <CircularProgress size={18} color="inherit" /> : (`Add to queue${queuePreview && queuePreview.count ? ` (you'll be #${queuePreview.count + 1})` : ''}`)}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Remove admin access?"
        message="This removes the admin's access to this organization's categories. Their account and any history are kept."
        onClose={() => { setDeleteDialogOpen(false); setUserToDelete(null); }}
        onConfirm={performDeleteCategoryUser}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        loading={deleteLoading}
      />
      <ConfirmDialog
        open={deleteAccountDialogOpen}
        title="Delete this user?"
        message="This permanently deletes the user's account. If they have historical records (appointments, etc.) the account is deactivated and its access removed instead, so their history is kept. This cannot be undone."
        onClose={() => { setDeleteAccountDialogOpen(false); setAccountToDelete(null); }}
        onConfirm={performDeleteUserAccount}
        confirmLabel="Delete user"
        cancelLabel="Cancel"
        loading={deleteAccountLoading}
      />

      <SendInviteDialog
        open={!!inviteFor}
        orgId={inviteFor?.orgId}
        category={inviteFor?.category}
        onClose={() => setInviteFor(null)}
      />
    </AdminErrorBoundary>
  );
}
